import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import { updateMarketData } from "../../store/tradeSlice";
import { updateUser, loginAsUser } from "../../store/authSlice";
import { fmtUSD, fmtP } from "../../utils/formatters";
import { INSTRUMENTS, getLotSize } from "../../utils/constants";
import { calcForexPnl } from "../../utils/forexUtils";
import { adminApi } from "../../services/api";
import api from "../../services/api";
import { Softphone } from "@frejun/softphone-web-sdk";

// ─── PHONE NORMALIZATION ─────────────────────────────────────────────────────
const normalizePhone = (raw) => {
  if (!raw) return raw;
  let s = String(raw).trim();
  s = s.replace(/[^\d+]/g, "");
  s = s.replace(/(?!^)\+/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (!s.startsWith("+")) s = "+" + s;
  return s;
};
// ─────────────────────────────────────────────────────────────────────────────

const calcOpenPositionPnl = (pos, currentPrice) => {
  const ls = getLotSize(pos.category || "crypto");
  const qty = Math.abs(Number(pos.qty)) || 0;
  const avgPrice = Number(pos.avgPrice) || 0;
  const isShort = pos.direction === "sell" || Number(pos.qty) < 0;
  const grossPnl = calcForexPnl(
    isShort,
    currentPrice,
    avgPrice,
    qty,
    pos.instrumentId,
    pos.category || "crypto",
    ls,
  );
  const commission = Number(pos.commission) || 0;
  return grossPnl - commission;
};

const TAG_OPTIONS = [
  "bronze",
  "silver",
  "gold",
  "diamond",
  "platinum",
  "black",
];
const LEVERAGE_OPTIONS = [
  "1:1",
  "1:10",
  "1:25",
  "1:50",
  "1:100",
  "1:200",
  "1:500",
];

const LEAD_STATUS_OPTIONS = [
  "new_lead",
  "pending",
  "called",
  "follow_up",
  "reached",
  "not_interested",
  "rejected",
  "forwarded",
  "account_made",
  "closed",
  "underage",
  "ftd",
  "call_back",
  "no_answer",
  "no_language",
];

const getTagColors = (tag) => {
  const colors = {
    bronze: "bg-amber-100 text-amber-700 border-amber-300",
    silver: "bg-gray-100 text-gray-700 border-gray-300",
    gold: "bg-yellow-100 text-yellow-700 border-yellow-300",
    diamond: "bg-cyan-100 text-cyan-700 border-cyan-300",
    platinum: "bg-purple-100 text-purple-700 border-purple-300",
    black: "bg-gray-900 text-white border-gray-700",
  };
  return colors[tag] || colors.bronze;
};

const KycDocumentModal = ({
  doc,
  onClose,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}) => {
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  if (!doc) return null;
  const isPdf = doc.filename?.toLowerCase().endsWith(".pdf");
  const docUrl = `/uploads/documents/${doc.filename}`;
  const docTypeLabel =
    {
      id: "Government ID",
      address: "Proof of Address",
      bank: "Bank Statement",
      selfie: "Selfie with ID",
    }[doc.type] || doc.type;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white border-2 border-[#e2e8f0] rounded-2xl w-full max-w-3xl mx-4 shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#eff6ff] border-2 border-[#bfdbfe] flex items-center justify-center">
              <i className="fas fa-file-alt text-[#1e3a5f] text-sm" />
            </div>
            <div>
              <div className="font-extrabold text-[#0f172a] text-sm uppercase tracking-wider">
                {docTypeLabel}
              </div>
              <div className="text-[10px] text-[#475569] font-mono font-bold">
                {doc.filename}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={docUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-xs font-mono font-extrabold hover:bg-[#0b1e33] transition-all flex items-center gap-1.5 uppercase tracking-wider"
            >
              <i className="fas fa-download text-[10px]" /> Download
            </a>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#dc2626] hover:border-[#dc2626] hover:bg-[#fef2f2] transition-all flex items-center justify-center"
            >
              <i className="fas fa-xmark text-sm" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-5 min-h-0">
          {isPdf ? (
            <iframe
              src={docUrl}
              title={doc.filename}
              className="w-full rounded-xl border-2 border-[#e2e8f0]"
              style={{ height: "420px" }}
            />
          ) : (
            <div className="flex items-center justify-center">
              <img
                src={docUrl}
                alt={docTypeLabel}
                className="max-w-full max-h-[420px] rounded-xl border-2 border-[#e2e8f0] object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="hidden w-full h-48 flex-col items-center justify-center text-[#64748b] gap-3 bg-[#f8fafc] rounded-xl border-2 border-[#e2e8f0]">
                <i className="fas fa-image-slash text-3xl opacity-30" />
                <span className="text-xs font-mono font-bold">
                  Failed to load image
                </span>
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1e3a5f] text-xs font-extrabold hover:underline"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t-2 border-[#e2e8f0] flex flex-wrap gap-4 text-[10px] text-[#475569] font-mono font-bold bg-[#f8fafc]">
          <span>
            <span className="text-[#0f172a] font-extrabold">Status:</span>{" "}
            <span
              className={
                doc.status === "approved"
                  ? "text-[#059669] font-extrabold"
                  : doc.status === "rejected"
                    ? "text-[#dc2626] font-extrabold"
                    : "text-[#d97706] font-extrabold"
              }
            >
              {doc.status?.toUpperCase()}
            </span>
          </span>
          {doc.uploadedAt && (
            <span>
              <span className="text-[#0f172a] font-extrabold">Uploaded:</span>{" "}
              {new Date(doc.uploadedAt).toLocaleString()}
            </span>
          )}
          {doc.originalName && (
            <span>
              <span className="text-[#0f172a] font-extrabold">Original:</span>{" "}
              {doc.originalName}
            </span>
          )}
        </div>
        {doc.status === "pending" && (onApprove || onReject) && (
          <div className="px-5 py-4 border-t-2 border-[#e2e8f0] bg-[#f8fafc]">
            {!showRejectInput ? (
              <div className="flex gap-3">
                {onApprove && (
                  <button
                    onClick={() => onApprove(doc)}
                    disabled={isApproving}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#059669] text-white text-sm font-extrabold hover:bg-[#047857] transition-all disabled:opacity-50 border-b-[3px] border-[#047857] uppercase tracking-wider"
                  >
                    {isApproving ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                        Approving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check" /> APPROVE DOCUMENT
                      </>
                    )}
                  </button>
                )}
                {onReject && (
                  <button
                    onClick={() => setShowRejectInput(true)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#dc2626] text-white text-sm font-extrabold hover:bg-[#b91c1c] transition-all border-b-[3px] border-[#b91c1c] uppercase tracking-wider"
                  >
                    <i className="fas fa-xmark" /> REJECT DOCUMENT
                  </button>
                )}
              </div>
            ) : (
              <div>
                <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                  Rejection Reason <span className="text-[#dc2626]">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  rows={2}
                  autoFocus
                  className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] font-mono text-sm font-extrabold outline-none focus:border-[#dc2626] focus:bg-white resize-none mb-3 placeholder:text-[#94a3b8]"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRejectInput(false);
                      setRejectReason("");
                    }}
                    className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-sm font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9]"
                  >
                    CANCEL
                  </button>
                  <button
                    onClick={() => onReject(doc, rejectReason)}
                    disabled={!rejectReason.trim() || isRejecting}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#dc2626] text-white text-sm font-extrabold hover:bg-[#b91c1c] transition-all disabled:opacity-50 border-b-[3px] border-[#b91c1c] uppercase tracking-wider"
                  >
                    {isRejecting ? (
                      <>
                        <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                        Rejecting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-xmark" /> CONFIRM REJECTION
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DepositProofModal = ({ deposit, onClose }) => {
  if (!deposit) return null;
  const details = deposit.methodDetails || {};
  const proofUrl =
    details.screenshotPath ||
    (details.screenshot ? `/uploads/deposits/${details.screenshot}` : null);

  const methodLabel =
    deposit.method === "bank_transfer"
      ? "Bank Transfer"
      : deposit.method === "custom_upi"
        ? "UPI 2"
        : deposit.method === "smilepayz"
          ? "UPI 1"
          : deposit.method || "Deposit";

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white border-2 border-[#e2e8f0] rounded-2xl w-full max-w-3xl mx-4 shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#eff6ff] border-2 border-[#bfdbfe] flex items-center justify-center">
              <i className="fas fa-receipt text-[#1e3a5f] text-sm" />
            </div>
            <div>
              <div className="font-extrabold text-[#0f172a] text-sm uppercase tracking-wider">
                {methodLabel} — Payment Proof
              </div>
              <div className="text-[10px] text-[#475569] font-mono font-bold">
                {deposit.transactionId || deposit.id}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {proofUrl && (
              <a
                href={proofUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-xs font-mono font-extrabold hover:bg-[#0b1e33] transition-all flex items-center gap-1.5 uppercase tracking-wider"
              >
                <i className="fas fa-download text-[10px]" /> Download
              </a>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] hover:text-[#dc2626] hover:border-[#dc2626] hover:bg-[#fef2f2] transition-all flex items-center justify-center"
            >
              <i className="fas fa-xmark text-sm" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 min-h-0">
          {!proofUrl ? (
            <div className="w-full h-48 flex flex-col items-center justify-center text-[#64748b] gap-3 bg-[#f8fafc] rounded-xl border-2 border-[#e2e8f0]">
              <i className="fas fa-image-slash text-3xl opacity-30" />
              <span className="text-xs font-mono font-bold">
                No screenshot uploaded for this deposit
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <img
                src={proofUrl}
                alt="Payment proof"
                className="max-w-full max-h-[520px] rounded-xl border-2 border-[#e2e8f0] object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  if (e.target.nextSibling)
                    e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="hidden w-full h-48 flex-col items-center justify-center text-[#64748b] gap-3 bg-[#f8fafc] rounded-xl border-2 border-[#e2e8f0]">
                <i className="fas fa-image-slash text-3xl opacity-30" />
                <span className="text-xs font-mono font-bold">
                  Failed to load screenshot
                </span>
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1e3a5f] text-xs font-extrabold hover:underline"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t-2 border-[#e2e8f0] flex flex-wrap gap-4 text-[10px] text-[#475569] font-mono font-bold bg-[#f8fafc]">
          <span>
            <span className="text-[#0f172a] font-extrabold">Amount:</span>{" "}
            <span className="text-[#059669] font-extrabold">
              +{fmtUSD(deposit.amount)}
            </span>
          </span>
          <span>
            <span className="text-[#0f172a] font-extrabold">Status:</span>{" "}
            <span
              className={
                deposit.status === "completed"
                  ? "text-[#059669] font-extrabold"
                  : deposit.status === "pending"
                    ? "text-[#d97706] font-extrabold"
                    : "text-[#dc2626] font-extrabold"
              }
            >
              {deposit.status?.toUpperCase()}
            </span>
          </span>
          {deposit.time && (
            <span>
              <span className="text-[#0f172a] font-extrabold">Date:</span>{" "}
              {new Date(deposit.time).toLocaleString()}
            </span>
          )}
          {details.utrNumber && (
            <span>
              <span className="text-[#0f172a] font-extrabold">UTR:</span>{" "}
              {details.utrNumber}
            </span>
          )}
          {details.upiId && (
            <span>
              <span className="text-[#0f172a] font-extrabold">UPI ID:</span>{" "}
              {details.upiId}
            </span>
          )}
          {details.paymentNote && (
            <span className="basis-full">
              <span className="text-[#0f172a] font-extrabold">Note:</span>{" "}
              {details.paymentNote}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { adminPermissions, adminUser } = useSelector((s) => s.admin);
  const { marketData } = useSelector((s) => s.trade);

  const [userDetails, setUserDetails] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [tradeSubTab, setTradeSubTab] = useState("open");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [swapRates, setSwapRates] = useState([]);

  const [leadData, setLeadData] = useState(null);
  const [leadComments, setLeadComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newCommentStatus, setNewCommentStatus] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  const [leadPhones, setLeadPhones] = useState([]);
  const [newPhoneInput, setNewPhoneInput] = useState("");
  const [newPhoneLabel, setNewPhoneLabel] = useState("");
  const [addingPhone, setAddingPhone] = useState(false);

  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editTradeId, setEditTradeId] = useState(null);
  const [editPnl, setEditPnl] = useState("");
  const [editSwap, setEditSwap] = useState("");
  const [editEntryPrice, setEditEntryPrice] = useState("");
  const [editExitPrice, setEditExitPrice] = useState("");
  const [editTradeOpenTime, setEditTradeOpenTime] = useState("");
  const [editTradeCloseTime, setEditTradeCloseTime] = useState("");
  const [editTradeQty, setEditTradeQty] = useState("");
  const [editPositionId, setEditPositionId] = useState(null);
  const [editPositionEntry, setEditPositionEntry] = useState("");
  const [editPositionQty, setEditPositionQty] = useState("");
  const [editPositionDirection, setEditPositionDirection] = useState("");
  const [editPositionTime, setEditPositionTime] = useState("");
  const [editPositionSl, setEditPositionSl] = useState("");
  const [editPositionTp, setEditPositionTp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [passwordHash, setPasswordHash] = useState("");
  const [showPasswordHash, setShowPasswordHash] = useState(false);

  const [kycViewDoc, setKycViewDoc] = useState(null);
  const [kycApproving, setKycApproving] = useState(false);
  const [kycRejecting, setKycRejecting] = useState(false);
  const [approvingAllKyc, setApprovingAllKyc] = useState(false);
  const [showApproveAllConfirm, setShowApproveAllConfirm] = useState(false);
  const [kycActionLoading, setKycActionLoading] = useState({});

  const [viewDepositProof, setViewDepositProof] = useState(null);

  const [tradeForm, setTradeForm] = useState({
    instrumentId: "BTCUSDT",
    category: "crypto",
    direction: "buy",
    quantity: 0.01,
    entryPrice: "",
    exitPrice: "",
    pnl: "",
    swap: "",
    time: "",
  });
  const [openTradeForm, setOpenTradeForm] = useState({
    instrumentId: "BTCUSDT",
    category: "crypto",
    direction: "buy",
    quantity: 0.01,
    entryPrice: "",
    time: "",
  });
  const [tradeMode, setTradeMode] = useState("closed");

  const [extraSwapInputs, setExtraSwapInputs] = useState({});
  const [depositActionLoading, setDepositActionLoading] = useState({});
  const [withdrawalActionLoading, setWithdrawalActionLoading] = useState({});
  const [expandedWithdrawal, setExpandedWithdrawal] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(null);
  const [closeConfirmLoading, setCloseConfirmLoading] = useState(false);
  const [rejectWithdrawalId, setRejectWithdrawalId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [deletingTradeId, setDeletingTradeId] = useState(null);
  const [deletingPositionId, setDeletingPositionId] = useState(null);
  const [loggingInAs, setLoggingInAs] = useState(false);

  const [emailSending, setEmailSending] = useState({});
  const [emailSent, setEmailSent] = useState({});

  const [withdrawalEmailModal, setWithdrawalEmailModal] = useState(null);
  const [withdrawalTransactionId, setWithdrawalTransactionId] = useState("");
  const [withdrawalEmailSending, setWithdrawalEmailSending] = useState({});
  const [withdrawalEmailSent, setWithdrawalEmailSent] = useState({});

  // ─── FREJUN SOFT PHONE STATE ───
  const [isCallActive, setIsCallActive] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const softphoneRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localAudioRef = useRef(null);
  const incomingTimeoutRef = useRef(null);
  const ringtoneRef = useRef(null);

  const FREJUN_TOKEN = import.meta.env.VITE_FREJUN_TOKEN;
  const FREJUN_EMAIL =
    adminUser?.frejunEmail ||
    import.meta.env.VITE_FREJUN_EMAIL ||
    "finance@groxion.com";
  const AGENT_VIRTUAL_NUMBER = adminUser?.frejunNumber || null;
  // ────────────────────────────────

  const tickTimerRef = useRef(null);
  const storeRef = useRef({});

  const isSuperAdmin =
    adminUser?.role === "super_admin" || adminUser?.role === "admin";
  const isStaff = adminUser?.role === "staff";
  const isManager = adminUser?.role === "manager";

  // ─── ROLE HELPERS ───
  const isTeamLead = () =>
    adminUser?.role === "team_lead" || adminUser?.role === "teamleader";

  // Admin, Super Admin, and Team Lead can see full details
  const canSeeFullDetails = () => {
    if (!adminUser) return false;
    return (
      adminUser.role === "admin" ||
      adminUser.role === "super_admin" ||
      adminUser.role === "team_lead" ||
      adminUser.role === "teamleader"
    );
  };

  const hasPerm = (key) => {
    if (isSuperAdmin) return true;
    if (isManager) {
      const managerPermissions = [
        "canModifyTrade",
        "canPlaceTrade",
        "canEditUser",
        "canSuspendUser",
        "canAdjustBalance",
        "canChangePassword",
        "canApplySwap",
        "canApproveWithdrawal",
        "canViewActions",
      ];
      return managerPermissions.includes(key);
    }
    return adminPermissions?.[key] === true;
  };

  useEffect(() => {
    fetchUserDetails();
    fetchOnlineUsers();
    fetchSwapRates();
    fetchLeadComments();
    startTickSimulation();
    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, [userId]);

  // ─── RINGTONE SETUP & UNLOCK ───
  useEffect(() => {
    ringtoneRef.current = new Audio("/the_fire_love.mp3");
    ringtoneRef.current.loop = true;
    ringtoneRef.current.preload = "auto";

    const unlockAudio = () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.volume = 0;
        ringtoneRef.current
          .play()
          .then(() => {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
            ringtoneRef.current.volume = 1;
          })
          .catch((e) => console.warn("[Audio] Unlock blocked:", e));
      }
      document.removeEventListener("click", unlockAudio);
    };

    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, []);

  const playRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch((e) => {
        console.error("[Audio] Play failed:", e);
        dispatch(
          showToast({
            message: "Please click anywhere to allow ringtone audio.",
            type: "warning",
          }),
        );
      });
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  const clearIncoming = () => {
    if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current);
    incomingTimeoutRef.current = null;
    stopRingtone();
    setIncomingCall(null);
  };

  // ─── INIT FREJUN SOFT PHONE ───
  useEffect(() => {
    if (!FREJUN_TOKEN || !adminUser?.frejunEmail) return;

    let isMounted = true;
    const initSoftphone = async () => {
      try {
        const softphone = new Softphone();
        await softphone.login({
          type: "OAuth2.0",
          token: FREJUN_TOKEN,
          email: FREJUN_EMAIL,
        });
        if (!isMounted) return;
        softphoneRef.current = softphone;

        const listeners = {
          onConnectionStateChange: () => {},
          onCallCreated: (type, details) => {
            if (type === "Incoming") {
              setIncomingCall({ lead: null });
              playRingtone();

              let callerNumber =
                details?.candidate || details?.request?.from?.uri?.user || "";
              if (callerNumber.includes("sip:")) {
                callerNumber = callerNumber.split("sip:")[1].split("@")[0];
              }

              if (incomingTimeoutRef.current)
                clearTimeout(incomingTimeoutRef.current);
              incomingTimeoutRef.current = setTimeout(
                () => setIncomingCall(null),
                60000,
              );

              if (callerNumber) {
                adminApi
                  .post("/leads/lookup-phone", { phone: callerNumber })
                  .then((res) => {
                    if (res.data?.found) {
                      setIncomingCall((prev) =>
                        prev ? { lead: res.data.lead } : prev,
                      );
                    }
                  })
                  .catch((err) => console.error("Lookup Failed:", err));
              }
            } else if (type === "Outgoing") {
              setIsCallActive(true);
            }
          },
          onCallRinging: () => {},
          onCallHangup: () => {
            setIsCallActive(false);
            clearIncoming();
          },
        };

        const audioElements = {
          remote: remoteAudioRef.current,
          local: localAudioRef.current,
        };
        await softphone.start(listeners, audioElements, {
          endSessionOnNavigate: false,
          logoutOnNavigate: false,
        });
      } catch (err) {
        console.error("[Softphone] init failed:", err);
        if (isMounted)
          dispatch(
            showToast({ message: "Dialer init failed.", type: "error" }),
          );
      }
    };

    initSoftphone();
    return () => {
      isMounted = false;
      softphoneRef.current?.logout();
      softphoneRef.current = null;
      stopRingtone();
    };
  }, [FREJUN_TOKEN, FREJUN_EMAIL, adminUser?.frejunEmail, dispatch]);

  // ─── DIAL HANDLERS ───
  const handleFrejunCall = async (phoneNumber, leadName) => {
    if (!phoneNumber || !softphoneRef.current) {
      dispatch(showToast({ message: "Dialer not ready.", type: "error" }));
      return;
    }
    const normalized = normalizePhone(phoneNumber);
    try {
      setIsCallActive(true);
      await softphoneRef.current.makeCall(
        normalized,
        AGENT_VIRTUAL_NUMBER || undefined,
        {},
      );
    } catch (err) {
      console.error("[Frejun] makeCall failed:", err);
      setIsCallActive(false);
      dispatch(
        showToast({
          message: `Call failed: ${err?.message || "unknown"}`,
          type: "error",
        }),
      );
    }
  };

  // Dial a SPECIFIC phone slot the staff clicked
  const handleDialSpecificPhone = (phoneNumber, leadName) => {
    if (!AGENT_VIRTUAL_NUMBER) {
      dispatch(
        showToast({ message: "No virtual number assigned.", type: "error" }),
      );
      return;
    }
    handleFrejunCall(phoneNumber, leadName);
  };

  const acceptCall = async () => {
    stopRingtone();
    try {
      await softphoneRef.current?.getSession?.accept();
      setIncomingCall(null);
      setIsCallActive(true);
    } catch (err) {
      dispatch(showToast({ message: "Accept failed", type: "error" }));
    }
  };

  const declineCall = async () => {
    stopRingtone();
    clearIncoming();
    setIsCallActive(false);
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
  };

  const handleHangup = async () => {
    try {
      await softphoneRef.current?.getSession?.end();
    } catch (e) {}
    setIsCallActive(false);
  };
  // ─────────────────────

  const fetchOnlineUsers = async () => {
    try {
      const res = await adminApi.get("/admin/users/online");
      setOnlineUsers(res.data.onlineUserIds || []);
    } catch (e) {}
  };
  const fetchSwapRates = async () => {
    try {
      const res = await adminApi.get("/admin/swap/rates");
      setSwapRates(res.data?.rates || []);
    } catch (e) {}
  };

  const fetchUserDetails = async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.get(`/admin/users/${userId}`);
      setUserDetails(res.data);
      setSelectedUser(res.data.user);
      setShowPasswordField(false);
      setNewPassword("");
      setShowPasswordHash(false);
    } catch (e) {
      dispatch(
        showToast({ message: "Failed to load user details", type: "error" }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeadComments = async () => {
    setIsLoadingComments(true);
    try {
      const res = await adminApi.get(`/leads/user/${userId}`);
      if (res.data?.lead) {
        setLeadData(res.data.lead);
        setLeadComments(res.data.lead.comments || []);
        setLeadPhones(res.data.lead.phones || []);
      }
    } catch (e) {
      setLeadData(null);
      setLeadComments([]);
      setLeadPhones([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleAddPhone = async () => {
    if (!leadData?.id) {
      dispatch(
        showToast({ message: "No lead linked to this user", type: "error" }),
      );
      return;
    }
    if (!newPhoneInput.trim()) {
      dispatch(
        showToast({ message: "Phone number is required", type: "error" }),
      );
      return;
    }
    if (leadPhones.length >= 3) {
      dispatch(
        showToast({
          message: "Maximum 3 phone numbers allowed",
          type: "error",
        }),
      );
      return;
    }

    setAddingPhone(true);
    try {
      const label = newPhoneLabel.trim() || null;
      const res = await adminApi.post(`/leads/${leadData.id}/phones`, {
        phone: newPhoneInput.trim(),
        label,
      });
      if (res.data?.phones) {
        setLeadPhones(res.data.phones);
      }
      dispatch(showToast({ message: "Phone added", type: "success" }));
      setNewPhoneInput("");
      setNewPhoneLabel("");
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to add phone",
          type: "error",
        }),
      );
    } finally {
      setAddingPhone(false);
    }
  };

  const copyPhoneToClipboard = (phone) => {
    navigator.clipboard
      .writeText(phone)
      .then(() => {
        dispatch(
          showToast({
            message: `Copied: ${phone}`,
            type: "success",
            duration: 2000,
          }),
        );
      })
      .catch(() => {});
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && !newCommentStatus) {
      dispatch(
        showToast({
          message: "Please enter a comment or select a status",
          type: "error",
        }),
      );
      return;
    }
    if (!leadData?.id) {
      dispatch(
        showToast({ message: "No lead linked to this user", type: "error" }),
      );
      return;
    }
    setIsAddingComment(true);
    try {
      const payload = { comment: newComment.trim() };
      if (newCommentStatus) payload.status = newCommentStatus;
      const res = await adminApi.put(`/leads/${leadData.id}/status`, payload);
      if (res.data?.lead) {
        setLeadData(res.data.lead);
        setLeadComments(res.data.lead.comments || []);
        setNewComment("");
        setNewCommentStatus("");
        dispatch(
          showToast({
            message: "Comment added to AnzoTrade Lead",
            type: "success",
          }),
        );
      }
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to add comment",
          type: "error",
        }),
      );
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleStatusChangeOnly = async (newStatus) => {
    if (!newStatus) return;
    if (!leadData?.id) {
      dispatch(
        showToast({ message: "No lead linked to this user", type: "error" }),
      );
      return;
    }
    setIsAddingComment(true);
    try {
      const res = await adminApi.put(`/leads/${leadData.id}/status`, {
        status: newStatus,
        comment: `Status changed to ${newStatus.replace(/_/g, " ").toUpperCase()}`,
      });
      if (res.data?.lead) {
        setLeadData(res.data.lead);
        setLeadComments(res.data.lead.comments || []);
        setNewCommentStatus("");
        dispatch(
          showToast({
            message: "Status updated in AnzoTrade Lead",
            type: "success",
          }),
        );
      }
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to update status",
          type: "error",
        }),
      );
    } finally {
      setIsAddingComment(false);
    }
  };

  const fetchPasswordHash = async () => {
    try {
      const res = await adminApi.get(`/admin/users/${userId}/password`);
      setPasswordHash(res.data?.password || "Not available");
      setShowPasswordHash(true);
    } catch (e) {
      dispatch(
        showToast({ message: "Failed to fetch password", type: "error" }),
      );
    }
  };

  const refreshUserDetails = async () => {
    if (!selectedUser) return;
    setIsRefreshing(true);
    try {
      const res = await adminApi.get(`/admin/users/${selectedUser.id}`);
      setUserDetails(res.data);
      setSelectedUser(res.data.user);
    } catch (e) {
      dispatch(showToast({ message: "Failed to refresh", type: "error" }));
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchMarketData = useCallback(async () => {
    try {
      const symbols = INSTRUMENTS.map((i) => i.id).join(",");
      const response = await api.get(`/market/quotes?symbols=${symbols}`);
      const raw = response.data;
      const dataMap = {};
      if (Array.isArray(raw)) {
        raw.forEach((q) => {
          if (q?.symbol) dataMap[q.symbol] = q;
        });
      } else if (raw && typeof raw === "object") {
        Object.entries(raw).forEach(([sym, q]) => {
          if (q?.price) dataMap[sym] = { ...q, price: q.price };
        });
      }
      storeRef.current = dataMap;
      dispatch(updateMarketData(dataMap));
    } catch (err) {}
  }, [dispatch]);

  const startTickSimulation = useCallback(() => {
    fetchMarketData();
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    tickTimerRef.current = setInterval(() => {
      fetchMarketData();
    }, 5000);
  }, [dispatch, fetchMarketData]);

  const getPrice = (instrumentId, avgPrice) =>
    marketData[instrumentId]?.price
      ? Number(marketData[instrumentId].price)
      : Number(avgPrice) || 0;
  const calcOpenPnl = (positions) => {
    let total = 0;
    positions.forEach((pos) => {
      const cp = getPrice(pos.instrumentId, pos.avgPrice);
      total += calcOpenPositionPnl(pos, cp);
    });
    return total;
  };
  const getNightsHeld = (createdAt) => {
    if (!createdAt) return 0;
    return Math.max(
      1,
      Math.ceil(
        (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24),
      ),
    );
  };
  const getSwapRate = (instrumentId, isLong) => {
    const rate = swapRates.find((r) => r.instrumentId === instrumentId);
    if (!rate || !rate.isActive) return 0;
    return isLong ? Number(rate.swapLongPerLot) : Number(rate.swapShortPerLot);
  };

  const handleApplyExtraSwap = async (position) => {
    const amount = extraSwapInputs[position.instrumentId];
    if (!amount || Number(amount) <= 0) {
      dispatch(showToast({ message: "Enter valid amount", type: "error" }));
      return;
    }
    try {
      await adminApi.post(`/admin/swap/apply/${selectedUser.id}`, {
        positionId: position.id,
        instrumentId: position.instrumentId,
        amount: Number(amount),
        nights: getNightsHeld(position.createdAt),
        ratePerLot: getSwapRate(
          position.instrumentId,
          position.direction === "buy",
        ),
        qtyLots: Math.abs(Number(position.qty)),
      });
      dispatch(
        showToast({
          message: `Swap of $${Number(amount).toFixed(2)} applied`,
          type: "success",
        }),
      );
      setExtraSwapInputs({ ...extraSwapInputs, [position.instrumentId]: "" });
      refreshUserDetails();
    } catch (e) {
      dispatch(showToast({ message: "Failed to apply swap", type: "error" }));
    }
  };

  const handleUpdateUser = async (field, value) => {
    if (!hasPerm("canEditUser")) {
      dispatch(showToast({ message: "Permission denied", type: "error" }));
      return;
    }
    try {
      if (field === "password") {
        await adminApi.post(`/admin/users/${userId}/reset-password`, {
          newPassword: value,
        });
        dispatch(showToast({ message: "Password reset", type: "success" }));
      } else {
        await adminApi.put(`/admin/users/${userId}/update`, { [field]: value });
        dispatch(showToast({ message: `${field} updated`, type: "success" }));
        if (field === "tag") dispatch(updateUser({ tag: value }));
      }
      setEditField(null);
      setShowPasswordField(false);
      setNewPassword("");
      fetchUserDetails();
    } catch (e) {
      dispatch(showToast({ message: "Failed", type: "error" }));
    }
  };

  const handleSuspendToggle = async (suspend) => {
    if (!hasPerm("canSuspendUser")) return;
    try {
      if (suspend)
        await adminApi.post(`/admin/users/${userId}/suspend`, {
          reason: "Admin action",
        });
      else await adminApi.post(`/admin/users/${userId}/activate`);
      dispatch(
        showToast({
          message: suspend ? "Suspended" : "Activated",
          type: "success",
        }),
      );
      refreshUserDetails();
    } catch (e) {
      dispatch(showToast({ message: "Failed", type: "error" }));
    }
  };

  const handleSaveTrade = async (tradeId) => {
    if (!hasPerm("canModifyTrade")) return;
    try {
      const payload = {};
      if (editPnl !== "") payload.pnl = Number(editPnl);
      if (editSwap !== "") payload.swap = Number(editSwap);
      if (editEntryPrice !== "") payload.entryPrice = Number(editEntryPrice);
      if (editExitPrice !== "") payload.exitPrice = Number(editExitPrice);
      if (editTradeQty !== "") payload.qty = Number(editTradeQty);
      if (editTradeOpenTime !== "") payload.openTime = editTradeOpenTime;
      if (editTradeCloseTime !== "") payload.closeTime = editTradeCloseTime;
      const res = await adminApi.put(`/admin/trades/${tradeId}`, payload);
      if (res.data?.trade) {
        setUserDetails((prev) => ({
          ...prev,
          recentTrades: prev.recentTrades.map((t) =>
            t.id === tradeId ? res.data.trade : t,
          ),
        }));
      }
      dispatch(showToast({ message: "Trade updated", type: "success" }));
      setEditTradeId(null);
      setEditPnl("");
      setEditSwap("");
      setEditEntryPrice("");
      setEditExitPrice("");
      setEditTradeOpenTime("");
      setEditTradeCloseTime("");
      setEditTradeQty("");
      refreshUserDetails();
    } catch (e) {
      dispatch(showToast({ message: "Failed", type: "error" }));
    }
  };

  const handleSavePosition = async (positionId) => {
    if (!hasPerm("canModifyTrade")) return;
    try {
      const payload = {};
      if (editPositionEntry !== "")
        payload.avgPrice = Number(editPositionEntry);
      if (editPositionQty !== "") payload.qty = Number(editPositionQty);
      if (editPositionDirection !== "")
        payload.direction = editPositionDirection;
      if (editPositionTime !== "") payload.createdAt = editPositionTime;
      if (editPositionSl !== "") payload.sl = Number(editPositionSl);
      if (editPositionTp !== "") payload.tp = Number(editPositionTp);
      const res = await adminApi.put(`/admin/positions/${positionId}`, payload);
      if (res.data?.position) {
        setUserDetails((prev) => ({
          ...prev,
          positions: prev.positions.map((p) =>
            p.id === positionId ? res.data.position : p,
          ),
        }));
      }
      dispatch(showToast({ message: "Position updated", type: "success" }));
      setEditPositionId(null);
      setEditPositionEntry("");
      setEditPositionQty("");
      setEditPositionDirection("");
      setEditPositionTime("");
      setEditPositionSl("");
      setEditPositionTp("");
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({ message: "Failed to update position", type: "error" }),
      );
    }
  };

  const handleClosePosition = (positionId) => {
    if (!hasPerm("canModifyTrade")) return;
    setShowCloseConfirm(positionId);
  };

  const confirmClosePosition = async () => {
    const positionId = showCloseConfirm;
    if (!positionId) return;
    setCloseConfirmLoading(true);
    try {
      const position = positions.find((p) => p.id === positionId);
      const currentPrice = position
        ? getPrice(position.instrumentId, position.avgPrice)
        : null;
      await adminApi.post(
        `/admin/positions/${positionId}/close`,
        currentPrice ? { closePrice: currentPrice } : {},
      );
      dispatch(
        showToast({ message: "Position closed successfully", type: "success" }),
      );
      setShowCloseConfirm(null);
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to close position",
          type: "error",
        }),
      );
    } finally {
      setCloseConfirmLoading(false);
    }
  };

  const handleDeleteTrade = async (tradeId) => {
    if (
      !window.confirm(
        "Delete this trade? This will reverse P&L from the user balance.",
      )
    )
      return;
    setDeletingTradeId(tradeId);
    try {
      await adminApi.delete(`/admin/trades/${tradeId}`);
      dispatch(
        showToast({ message: "Trade deleted successfully", type: "success" }),
      );
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to delete trade",
          type: "error",
        }),
      );
    } finally {
      setDeletingTradeId(null);
    }
  };

  const handleDeletePosition = async (positionId) => {
    if (
      !window.confirm(
        "Delete this position? The margin will be released back to the user.",
      )
    )
      return;
    setDeletingPositionId(positionId);
    try {
      const res = await adminApi.delete(`/admin/positions/${positionId}`);
      dispatch(
        showToast({
          message: `Position deleted. $${res.data?.releasedMargin?.toFixed(2) || "0"} margin released.`,
          type: "success",
        }),
      );
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to delete position",
          type: "error",
        }),
      );
    } finally {
      setDeletingPositionId(null);
    }
  };

  const handleLoginAsUser = async () => {
    if (
      !window.confirm(
        `Login as ${u?.firstName} ${u?.lastName}? You'll be redirected to their trading terminal.`,
      )
    )
      return;
    setLoggingInAs(true);
    try {
      const res = await adminApi.post(`/admin/users/${userId}/login-as`);
      dispatch(loginAsUser({ user: res.data.user, token: res.data.token }));
      localStorage.setItem("refreshToken", res.data.refreshToken);
      dispatch(
        showToast({
          message: `Logged in as ${res.data.user.firstName} ${res.data.user.lastName}`,
          type: "success",
        }),
      );
      setTimeout(() => {
        window.location.href = "/terminal";
      }, 300);
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to login as user",
          type: "error",
        }),
      );
      setLoggingInAs(false);
    }
  };

  const handlePlaceClosedTrade = async () => {
    if (!hasPerm("canPlaceTrade")) return;
    if (!tradeForm.entryPrice || !tradeForm.exitPrice) {
      dispatch(
        showToast({ message: "Entry and exit required", type: "error" }),
      );
      return;
    }
    try {
      await adminApi.post(`/admin/users/${userId}/trade`, {
        instrumentId: tradeForm.instrumentId,
        category: tradeForm.category,
        direction: tradeForm.direction,
        quantity: Number(tradeForm.quantity),
        entryPrice: Number(tradeForm.entryPrice),
        exitPrice: Number(tradeForm.exitPrice),
        pnl: Number(tradeForm.pnl) || 0,
        swap: Number(tradeForm.swap) || 0,
        status: "closed",
        time: tradeForm.time || undefined,
      });
      dispatch(showToast({ message: "Closed trade placed", type: "success" }));
      setTradeForm({
        instrumentId: "BTCUSDT",
        category: "crypto",
        direction: "buy",
        quantity: 0.01,
        entryPrice: "",
        exitPrice: "",
        pnl: "",
        swap: "",
        time: "",
      });
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed",
          type: "error",
        }),
      );
    }
  };

  const handlePlaceOpenTrade = async () => {
    if (!hasPerm("canPlaceTrade")) return;
    if (!openTradeForm.entryPrice) {
      dispatch(showToast({ message: "Entry price required", type: "error" }));
      return;
    }
    try {
      const res = await adminApi.post(`/admin/users/${userId}/trade`, {
        instrumentId: openTradeForm.instrumentId,
        category: openTradeForm.category,
        direction: openTradeForm.direction,
        quantity: Number(openTradeForm.quantity),
        entryPrice: Number(openTradeForm.entryPrice),
        status: "open",
        time: openTradeForm.time || undefined,
      });
      dispatch(
        showToast({
          message: `Open ${openTradeForm.direction.toUpperCase()} trade placed · Margin: $${(res.data.margin || 0).toFixed(2)}`,
          type: "success",
        }),
      );
      setOpenTradeForm({
        instrumentId: "BTCUSDT",
        category: "crypto",
        direction: "buy",
        quantity: 0.01,
        entryPrice: "",
        time: "",
      });
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to place open trade",
          type: "error",
        }),
      );
    }
  };

  const handleConfirmDeposit = async (depositId) => {
    setDepositActionLoading((prev) => ({ ...prev, [depositId]: "confirming" }));
    try {
      await adminApi.put(`/deposit/bank-transfer/${depositId}/confirm`);
      dispatch(
        showToast({
          message: "Bank transfer confirmed and credited",
          type: "success",
        }),
      );
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to confirm deposit",
          type: "error",
        }),
      );
    } finally {
      setDepositActionLoading((prev) => ({ ...prev, [depositId]: null }));
    }
  };

  const handleRejectDeposit = async (depositId) => {
    const reason = window.prompt("Rejection reason (optional):");
    if (reason === null) return;
    setDepositActionLoading((prev) => ({ ...prev, [depositId]: "rejecting" }));
    try {
      await adminApi.put(`/deposit/bank-transfer/${depositId}/reject`, {
        reason: reason || "Rejected by admin",
      });
      dispatch(
        showToast({ message: "Bank transfer rejected", type: "success" }),
      );
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to reject deposit",
          type: "error",
        }),
      );
    } finally {
      setDepositActionLoading((prev) => ({ ...prev, [depositId]: null }));
    }
  };

  const handleSendEmail = async (depositId) => {
    setEmailSending((prev) => ({ ...prev, [depositId]: true }));
    try {
      await adminApi.post(`/admin/deposits/${depositId}/send-email`);
      dispatch(
        showToast({
          message: "Deposit confirmation email sent!",
          type: "success",
        }),
      );
      setEmailSent((prev) => ({ ...prev, [depositId]: true }));
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to send email",
          type: "error",
        }),
      );
    } finally {
      setEmailSending((prev) => ({ ...prev, [depositId]: false }));
    }
  };

  const openWithdrawalEmailModal = (withdrawalId) => {
    setWithdrawalEmailModal(withdrawalId);
    setWithdrawalTransactionId("");
  };

  const handleSendWithdrawalEmail = async () => {
    if (!withdrawalTransactionId.trim()) {
      dispatch(
        showToast({ message: "Please enter a Transaction ID", type: "error" }),
      );
      return;
    }
    const wId = withdrawalEmailModal;
    setWithdrawalEmailSending((prev) => ({ ...prev, [wId]: true }));
    try {
      await adminApi.post(`/admin/withdrawals/${wId}/send-email`, {
        transactionId: withdrawalTransactionId.trim(),
      });
      dispatch(
        showToast({
          message: "Withdrawal confirmation email sent!",
          type: "success",
        }),
      );
      setWithdrawalEmailSent((prev) => ({ ...prev, [wId]: true }));
      setWithdrawalEmailModal(null);
      setWithdrawalTransactionId("");
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to send email",
          type: "error",
        }),
      );
    } finally {
      setWithdrawalEmailSending((prev) => ({ ...prev, [wId]: false }));
    }
  };

  const handleApproveWithdrawal = async (withdrawalId) => {
    setWithdrawalActionLoading((prev) => ({
      ...prev,
      [withdrawalId]: "approving",
    }));
    try {
      await adminApi.post(`/admin/withdrawals/${withdrawalId}/approve`);
      dispatch(showToast({ message: "Withdrawal approved", type: "success" }));
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to approve",
          type: "error",
        }),
      );
    } finally {
      setWithdrawalActionLoading((prev) => ({ ...prev, [withdrawalId]: null }));
    }
  };

  const handleRejectWithdrawal = (withdrawalId) => {
    setRejectWithdrawalId(withdrawalId);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const confirmRejectWithdrawal = async () => {
    if (!rejectReason.trim()) {
      dispatch(
        showToast({
          message: "Please enter a rejection reason",
          type: "error",
        }),
      );
      return;
    }
    setWithdrawalActionLoading((prev) => ({
      ...prev,
      [rejectWithdrawalId]: "rejecting",
    }));
    try {
      await adminApi.post(`/admin/withdrawals/${rejectWithdrawalId}/reject`, {
        reason: rejectReason,
      });
      dispatch(showToast({ message: "Withdrawal rejected", type: "success" }));
      setShowRejectModal(false);
      setRejectWithdrawalId(null);
      setRejectReason("");
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to reject",
          type: "error",
        }),
      );
    } finally {
      setWithdrawalActionLoading((prev) => ({
        ...prev,
        [rejectWithdrawalId]: null,
      }));
    }
  };

  const handleKycApprove = async (doc) => {
    if (!doc || !doc.type) {
      dispatch(
        showToast({ message: "Document type not found", type: "error" }),
      );
      return;
    }
    setKycActionLoading((prev) => ({ ...prev, [doc.type]: "approving" }));
    try {
      await adminApi.post(`/admin/kyc/${userId}/approve`, {
        documentType: doc.type,
      });
      dispatch(
        showToast({
          message: `${doc.type.toUpperCase()} document approved`,
          type: "success",
        }),
      );
      setKycViewDoc(null);
      refreshUserDetails();
    } catch (e) {
      console.error("KYC approve error:", e.response?.data || e.message);
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to approve document",
          type: "error",
        }),
      );
    } finally {
      setKycActionLoading((prev) => ({ ...prev, [doc.type]: null }));
      setKycApproving(false);
    }
  };

  const handleKycReject = async (doc, reason) => {
    if (!reason?.trim()) {
      dispatch(
        showToast({ message: "Rejection reason required", type: "error" }),
      );
      return;
    }
    setKycActionLoading((prev) => ({ ...prev, [doc.type]: "rejecting" }));
    try {
      await adminApi.post(`/admin/kyc/${userId}/reject`, {
        documentType: doc.type,
        reason,
      });
      dispatch(
        showToast({
          message: `${doc.type.toUpperCase()} document rejected`,
          type: "success",
        }),
      );
      setKycViewDoc(null);
      refreshUserDetails();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to reject document",
          type: "error",
        }),
      );
    } finally {
      setKycActionLoading((prev) => ({ ...prev, [doc.type]: null }));
      setKycRejecting(false);
    }
  };

  const handleApproveAllKyc = async () => {
    if (!hasPerm("canEditUser")) {
      dispatch(showToast({ message: "Permission denied", type: "error" }));
      return;
    }
    setApprovingAllKyc(true);
    try {
      const docsToApprove = documents.filter(
        (d) => d.status !== "approved" && d.status !== "rejected",
      );

      if (docsToApprove.length === 0) {
        dispatch(
          showToast({
            message: "No pending documents to approve",
            type: "info",
          }),
        );
        setShowApproveAllConfirm(false);
        return;
      }

      const results = await Promise.allSettled(
        docsToApprove.map((doc) =>
          adminApi.post(`/admin/kyc/${userId}/approve`, {
            documentType: doc.type,
          }),
        ),
      );

      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;

      if (failed > 0) {
        console.error(
          "KYC approval failures:",
          results.filter((r) => r.status === "rejected").map((r) => r.reason),
        );
        dispatch(
          showToast({
            message: `${succeeded} approved, ${failed} failed`,
            type: succeeded > 0 ? "success" : "error",
          }),
        );
      } else {
        dispatch(
          showToast({
            message: `Approved ${succeeded} KYC documents!`,
            type: "success",
          }),
        );
      }

      setShowApproveAllConfirm(false);
      refreshUserDetails();
    } catch (e) {
      console.error("Approve all KYC error:", e);
      dispatch(
        showToast({
          message:
            e.response?.data?.message || "Failed to approve all documents",
          type: "error",
        }),
      );
    } finally {
      setApprovingAllKyc(false);
    }
  };

  const getOpenTradePnlPreview = () => {
    const entry = Number(openTradeForm.entryPrice);
    if (!entry || !openTradeForm.instrumentId) return null;
    const currentPrice = getPrice(openTradeForm.instrumentId, entry);
    const ls = getLotSize(openTradeForm.category);
    const qty = Number(openTradeForm.quantity) || 0;
    const isShort = openTradeForm.direction === "sell";
    return calcForexPnl(
      isShort,
      currentPrice,
      entry,
      qty,
      openTradeForm.instrumentId,
      openTradeForm.category,
      ls,
    );
  };
  const isOnline = (id) => onlineUsers.includes(id);
  const groupedInstruments = {};
  INSTRUMENTS.forEach((inst) => {
    if (!groupedInstruments[inst.category])
      groupedInstruments[inst.category] = [];
    groupedInstruments[inst.category].push(inst);
  });
  const categoryLabels = {
    forex: "Forex",
    crypto: "Crypto",
    metal: "Metals",
    stock: "Stocks",
    index: "Indices",
    commodity: "Commodities",
  };

  if (isLoading)
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (!selectedUser || !userDetails)
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-[#0f172a] font-extrabold">
        User not found
      </div>
    );

  const u = selectedUser;
  const trades = userDetails.recentTrades || [];
  const positions = userDetails.positions || [];
  const closedTrades = trades.filter((t) => t.status === "closed");
  const deposits = userDetails.deposits || [];
  const withdrawals = userDetails.withdrawals || [];
  const documents = userDetails.documents || [];
  const swapHistory = userDetails.swapHistory || [];
  const stats = userDetails.stats || {};

  const balance = Number(u.balance) || 0;
  const creditLimit = Number(u.creditLimit) || 0;
  const bonusBalance = Number(u.bonusBalance) || 0;
  const openPnl = calcOpenPnl(positions);
  const equity = balance + creditLimit + bonusBalance + openPnl;
  const totalMargin = positions.reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const freeMargin = equity - totalMargin;
  const marginLevel = totalMargin > 0 ? (equity / totalMargin) * 100 : 0;
  const creditUsed = Math.min(
    creditLimit,
    Math.max(0, totalMargin - balance - bonusBalance),
  );
  const creditAvailable = creditLimit - creditUsed;
  const totalSwap = trades.reduce((s, t) => s + (Number(t.swap) || 0), 0);
  const overnightPositions = positions.filter(
    (p) => getNightsHeld(p.createdAt) >= 1,
  );

  const tabs = ["overview", "trades", "questionnaire"];
  if (overnightPositions.length > 0) tabs.push("overnight");
  tabs.push("deposits", "withdrawals", "kyc");
  if (hasPerm("canViewActions")) tabs.push("actions");

  const getMarginLevelColor = () => {
    if (totalMargin === 0) return "text-[#64748b]";
    if (marginLevel > 200) return "text-[#059669]";
    if (marginLevel > 100) return "text-[#d97706]";
    return "text-[#dc2626]";
  };

  const kycDocTypes = [
    {
      type: "id",
      icon: "fa-passport",
      label: "Government ID",
      verifiedKey: "idVerified",
    },
    {
      type: "address",
      icon: "fa-file-alt",
      label: "Proof of Address",
      verifiedKey: "addressVerified",
    },
    {
      type: "bank",
      icon: "fa-bank",
      label: "Bank Statement",
      verifiedKey: null,
    },
    {
      type: "selfie",
      icon: "fa-camera",
      label: "Selfie with ID",
      verifiedKey: "selfieVerified",
    },
  ];

  const formatCommentDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getLeadStatusBadge = (status) => {
    const colors = {
      new_lead: "bg-[#059669] text-white border-[#047857]",
      pending: "bg-[#d97706] text-white border-[#b45309]",
      called: "bg-[#2563eb] text-white border-[#1d4ed8]",
      follow_up: "bg-[#7c3aed] text-white border-[#6d28d9]",
      reached: "bg-[#0d9488] text-white border-[#0f766e]",
      not_interested: "bg-[#64748b] text-white border-[#475569]",
      rejected: "bg-[#dc2626] text-white border-[#b91c1c]",
      forwarded: "bg-[#1e3a5f] text-white border-[#0b1e33]",
      account_made: "bg-[#059669] text-white border-[#047857]",
      underage: "bg-[#dc2626] text-white border-[#b91c1c]",
      ftd: "bg-[#0d9488] text-white border-[#0f766e]",
      call_back: "bg-[#d97706] text-white border-[#b45309]",
      no_answer: "bg-[#d97706] text-white border-[#b45309]",
      no_language: "bg-[#ec4899] text-white border-[#db2777]",
      closed: "bg-[#64748b] text-white border-[#475569]",
    };
    const displayName =
      status === "new_lead"
        ? "NEW LEAD"
        : status
          ? status.replace(/_/g, " ").toUpperCase()
          : "NEW LEAD";
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[9px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${colors[status] || "bg-[#94a3b8] text-white border-[#64748b]"}`}
      >
        {displayName}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <audio ref={remoteAudioRef} autoPlay />
      <audio ref={localAudioRef} autoPlay muted />

      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] rounded-full bg-gradient-radial from-[rgba(30,58,95,0.03)] to-transparent top-[-260px] right-[-200px] animate-float" />
        <div className="absolute w-[600px] h-[600px] rounded-full bg-gradient-radial from-[rgba(59,130,246,0.02)] to-transparent bottom-[-200px] left-[-160px] animate-float animation-delay-2000" />
      </div>

      {/* Topbar */}
      <div className="fixed top-0 left-0 right-0 h-[60px] z-[200] bg-white border-b-2 border-[#e2e8f0] flex items-center justify-between px-5 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne font-extrabold text-[11px] uppercase tracking-wider hover:text-[#1e3a5f] hover:border-[#1e3a5f] transition-all"
          >
            <i className="fas fa-arrow-left" /> BACK
          </button>
          <div className="w-px h-6 bg-[#e2e8f0]" />
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center text-white font-extrabold text-sm shadow-md shadow-[#1e3a5f]/25">
              {u.firstName?.[0]}
              {u.lastName?.[0]}
            </div>
            <div>
              <div className="font-extrabold text-[#0f172a] text-lg">
                {u.firstName} {u.lastName}
              </div>
              <div className="text-sm text-[#475569] font-bold">
                {canSeeFullDetails() ? u.email : "****@****"}
              </div>
            </div>
            {isOnline(u.id) ? (
              <span className="px-3 py-1 rounded-lg bg-[#ecfdf5] text-[#059669] text-[10px] font-mono font-extrabold flex items-center gap-1.5 border-2 border-[#a7f3d0] uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-[#059669]" /> ONLINE
              </span>
            ) : (
              <span className="px-3 py-1 rounded-lg bg-[#f8fafc] text-[#94a3b8] text-[10px] font-mono font-extrabold border-2 border-[#e2e8f0] uppercase tracking-wider">
                OFFLINE
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleLoginAsUser}
            disabled={loggingInAs}
            className="px-4 py-2 rounded-lg bg-[#1e3a5f] text-white text-xs font-mono font-extrabold hover:bg-[#0b1e33] transition-all disabled:opacity-50 flex items-center gap-1.5 uppercase tracking-wider border-b-[2px] border-[#0b1e33]"
          >
            {loggingInAs ? (
              <>
                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />{" "}
                LOGGING IN...
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt" /> LOGIN AS
              </>
            )}
          </button>
          <button
            onClick={refreshUserDetails}
            disabled={isRefreshing}
            className="px-4 py-2 rounded-lg bg-[#1e3a5f] text-white text-xs font-mono font-extrabold hover:bg-[#0b1e33] transition-all disabled:opacity-50 uppercase tracking-wider border-b-[2px] border-[#0b1e33]"
          >
            <i
              className={`fas fa-sync-alt mr-1.5 ${isRefreshing ? "animate-spin" : ""}`}
            />
            REFRESH
          </button>
          {hasPerm("canSuspendUser") &&
            (!u.isSuspended ? (
              <button
                onClick={() => handleSuspendToggle(true)}
                className="px-4 py-2 rounded-lg bg-[#dc2626] text-white text-xs font-mono font-extrabold hover:bg-[#b91c1c] transition-all uppercase tracking-wider border-b-[2px] border-[#b91c1c]"
              >
                <i className="fas fa-ban mr-1.5" /> SUSPEND
              </button>
            ) : (
              <button
                onClick={() => handleSuspendToggle(false)}
                className="px-4 py-2 rounded-lg bg-[#059669] text-white text-xs font-mono font-extrabold hover:bg-[#047857] transition-all uppercase tracking-wider border-b-[2px] border-[#047857]"
              >
                <i className="fas fa-check mr-1.5" /> ACTIVATE
              </button>
            ))}
        </div>
      </div>
      <div className="pt-[60px]">
        {/* Tabs */}
        <div className="border-b-2 border-[#e2e8f0] bg-[#f8fafc] px-6 flex gap-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3.5 text-[11px] font-syne font-extrabold border-b-[3px] transition-all capitalize whitespace-nowrap uppercase tracking-wider ${activeTab === tab ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-[#94a3b8] hover:text-[#475569]"}`}
            >
              {tab}
              {tab === "trades" && (
                <span className="ml-2 text-[10px] bg-[#1e3a5f] text-white px-2 py-0.5 rounded-full font-extrabold">
                  {positions.length + closedTrades.length}
                </span>
              )}
              {tab === "overnight" && (
                <span className="ml-2 text-[10px] bg-[#d97706] text-white px-2 py-0.5 rounded-full font-extrabold">
                  {overnightPositions.length}
                </span>
              )}
              {tab === "kyc" && (
                <span className="ml-2 text-[10px] bg-[#1e3a5f] text-white px-2 py-0.5 rounded-full font-extrabold">
                  {documents.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6 mx-auto">
          {/* ── OVERVIEW TAB ── */}
          {activeTab === "overview" && (
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
                <div className="space-y-6 min-w-0">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      {
                        label: "BALANCE",
                        value: fmtUSD(balance),
                        color: "#059669",
                        icon: "fa-wallet",
                      },
                      {
                        label: "CREDIT LIMIT",
                        value: fmtUSD(creditLimit),
                        color: "#1e3a5f",
                        icon: "fa-credit-card",
                      },
                      {
                        label: "CREDIT USED",
                        value: fmtUSD(creditUsed),
                        color:
                          creditUsed > 0
                            ? creditUsed / creditLimit > 0.8
                              ? "#dc2626"
                              : "#d97706"
                            : "#64748b",
                        icon: "fa-credit-card",
                      },
                      {
                        label: "CREDIT AVAIL",
                        value: fmtUSD(creditAvailable),
                        color: creditAvailable > 0 ? "#059669" : "#dc2626",
                        icon: "fa-unlock",
                      },
                      {
                        label: "BONUS",
                        value: fmtUSD(bonusBalance),
                        color: "#2563eb",
                        icon: "fa-gift",
                      },
                      {
                        label: "TOTAL DEPOSITS",
                        value: fmtUSD(stats.totalDeposits || 0),
                        color: "#1e3a5f",
                        icon: "fa-circle-plus",
                      },
                      {
                        label: "TOTAL WITHDRAWALS",
                        value: fmtUSD(stats.totalWithdrawals || 0),
                        color: "#dc2626",
                        icon: "fa-circle-minus",
                      },
                      {
                        label: "TOTAL P&L",
                        value:
                          ((stats.totalTradingPnl || 0) >= 0 ? "+" : "") +
                          fmtUSD(stats.totalTradingPnl || 0),
                        color:
                          (stats.totalTradingPnl || 0) >= 0
                            ? "#059669"
                            : "#dc2626",
                        icon: "fa-chart-line",
                      },
                      {
                        label: "OPEN P&L",
                        value: (openPnl >= 0 ? "+" : "") + fmtUSD(openPnl),
                        color: openPnl >= 0 ? "#059669" : "#dc2626",
                        icon: "fa-chart-bar",
                      },
                      {
                        label: "EQUITY",
                        value: fmtUSD(equity),
                        color: equity >= 0 ? "#059669" : "#dc2626",
                        icon: "fa-scale-balanced",
                      },
                      {
                        label: "MARGIN USED",
                        value: fmtUSD(totalMargin),
                        color: "#2563eb",
                        icon: "fa-lock",
                      },
                      {
                        label: "FREE MARGIN",
                        value: fmtUSD(freeMargin),
                        color: freeMargin >= 0 ? "#0d9488" : "#dc2626",
                        icon: "fa-gauge",
                      },
                      {
                        label: "MARGIN LEVEL",
                        value:
                          totalMargin > 0 ? marginLevel.toFixed(1) + "%" : "—",
                        color: getMarginLevelColor(),
                        icon: "fa-percent",
                      },
                      {
                        label: "TOTAL SWAP",
                        value: "-$" + totalSwap.toFixed(2),
                        color: "#d97706",
                        icon: "fa-rotate",
                      },
                      {
                        label: "TOTAL TRADES",
                        value: stats.totalTrades || 0,
                        color: "#2563eb",
                        icon: "fa-exchange-alt",
                      },
                      {
                        label: "STATUS",
                        value: u.isSuspended
                          ? "SUSPENDED"
                          : u.isVerified
                            ? "VERIFIED"
                            : "UNVERIFIED",
                        color: u.isSuspended
                          ? "#dc2626"
                          : u.isVerified
                            ? "#059669"
                            : "#64748b",
                        icon: "fa-shield-alt",
                      },
                    ].map((card) => (
                      <div
                        key={card.label}
                        className="bg-white border-2 border-[#e2e8f0] rounded-xl p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ background: `${card.color}15` }}
                          >
                            <i
                              className={`fas ${card.icon} text-sm`}
                              style={{ color: card.color }}
                            />
                          </div>
                          <span className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider">
                            {card.label}
                          </span>
                        </div>
                        <div
                          className={`text-xl font-extrabold ${card.label === "MARGIN LEVEL" ? "text-black" : ""}`}
                          style={
                            card.label !== "MARGIN LEVEL"
                              ? { color: card.color }
                              : {}
                          }
                        >
                          {card.value}
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalMargin > 0 && marginLevel <= 120 && (
                    <div className="p-4 bg-[#fef2f2] border-[3px] border-[#dc2626] rounded-xl">
                      <p className="text-[11px] text-[#dc2626] font-syne font-extrabold flex items-center gap-2 uppercase tracking-wider">
                        <i className="fas fa-exclamation-triangle" />
                        {marginLevel <= 100
                          ? "⚠️ MARGIN CALL LEVEL — ADD FUNDS OR CLOSE POSITIONS"
                          : "⚠️ LOW MARGIN — BE CAREFUL"}
                      </p>
                    </div>
                  )}

                  <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                    <h3 className="font-extrabold text-[#0b1e33] mb-4 flex items-center gap-2 uppercase tracking-tight text-base">
                      <i className="fas fa-user text-[#1e3a5f]" /> PERSONAL
                      INFORMATION
                      {hasPerm("canEditUser") && (
                        <button
                          onClick={() => setEditField("all")}
                          className="ml-auto text-xs text-[#1e3a5f] font-syne font-extrabold hover:underline uppercase tracking-wider"
                        >
                          EDIT ALL
                        </button>
                      )}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {[
                        {
                          label: "FIRST NAME",
                          value: u.firstName,
                          field: "firstName",
                        },
                        {
                          label: "LAST NAME",
                          value: u.lastName,
                          field: "lastName",
                        },
                        {
                          label: "EMAIL",
                          value: canSeeFullDetails() ? u.email : "****@****",
                          field: "email",
                        },
                        {
                          label: "PHONE",
                          value: canSeeFullDetails() ? (
                            u.phone ? (
                              <a
                                href={`tel:${u.phone}`}
                                className="text-[#2563eb] font-extrabold hover:underline"
                              >
                                {u.phone}
                              </a>
                            ) : (
                              "—"
                            )
                          ) : u.phone ? (
                            <button
                              onClick={() =>
                                handleDialSpecificPhone(
                                  u.phone,
                                  `${u.firstName} ${u.lastName}`,
                                )
                              }
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0] hover:bg-[#d1fae5] transition-all font-extrabold text-[11px]"
                            >
                              <i className="fas fa-phone-alt text-[9px]" /> CALL
                            </button>
                          ) : (
                            "—"
                          ),
                          field: "phone",
                        },
                        {
                          label: "PASSWORD",
                          value: (
                            <span
                              className="text-[#64748b] cursor-pointer hover:text-[#1e3a5f] font-bold"
                              onClick={fetchPasswordHash}
                            >
                              {showPasswordHash ? (
                                <span className="font-mono text-[10px] text-[#1e3a5f] font-extrabold">
                                  {passwordHash}
                                </span>
                              ) : (
                                "••••••••"
                              )}
                            </span>
                          ),
                          field: "password",
                        },
                        {
                          label: "DATE OF BIRTH",
                          value: u.dateOfBirth
                            ? new Date(u.dateOfBirth).toLocaleDateString()
                            : "—",
                          field: "dateOfBirth",
                        },
                        {
                          label: "COUNTRY",
                          value: u.country || "—",
                          field: "country",
                        },
                        { label: "CITY", value: u.city || "—", field: "city" },
                        {
                          label: "ADDRESS",
                          value: u.address || "—",
                          field: "address",
                        },
                        {
                          label: "LEVERAGE",
                          value: u.leverage || "1:100",
                          field: "leverage",
                        },
                        {
                          label: "TAG",
                          value: (
                            <span
                              className={`px-2 py-0.5 rounded text-[9px] font-mono font-extrabold border ${getTagColors(u.tag)}`}
                            >
                              {(u.tag || "bronze").toUpperCase()}
                            </span>
                          ),
                          field: "tag",
                        },
                        {
                          label: "JOINED",
                          value: u.createdAt
                            ? new Date(u.createdAt).toLocaleString()
                            : "—",
                        },
                        {
                          label: "LAST ACTIVE",
                          value: u.lastActive
                            ? new Date(u.lastActive).toLocaleString()
                            : "—",
                        },
                        {
                          label: "USER ID",
                          value: (
                            <span className="font-mono text-xs text-[#64748b] font-bold">
                              {u.id}
                            </span>
                          ),
                        },
                      ].map((item) => (
                        <div key={item.label}>
                          <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                            {item.label}
                          </div>
                          <div className="font-extrabold text-[#0f172a] flex items-center gap-2">
                            {item.value}
                            {item.field &&
                              item.field !== "password" &&
                              hasPerm("canEditUser") && (
                                <button
                                  onClick={() => {
                                    setEditField(item.field);
                                    setEditValue(
                                      typeof item.value === "string"
                                        ? item.value.replace("—", "")
                                        : "",
                                    );
                                  }}
                                  className="text-[#1e3a5f] hover:text-[#0b1e33] text-xs"
                                >
                                  <i className="fas fa-pen text-[10px]" />
                                </button>
                              )}
                            {item.field === "password" &&
                              hasPerm("canEditUser") && (
                                <button
                                  onClick={() =>
                                    setShowPasswordField(!showPasswordField)
                                  }
                                  className="text-[#1e3a5f] hover:text-[#0b1e33] text-xs"
                                >
                                  <i className="fas fa-pen text-[10px]" />
                                </button>
                              )}
                          </div>
                        </div>
                      ))}

                      {leadData && (
                        <div className="col-span-4 mt-2 p-4 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-xl">
                          <div className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider mb-3 flex items-center gap-2">
                            <i className="fas fa-phone" /> PHONE NUMBERS (
                            {leadPhones.length}/3)
                          </div>

                          <div className="grid grid-cols-3 gap-3 mb-3">
                            {[0, 1, 2].map((slotIndex) => {
                              const phone = leadPhones[slotIndex];
                              return (
                                <div
                                  key={slotIndex}
                                  className={`bg-white border-2 rounded-lg px-3 py-2.5 flex items-center justify-between ${phone ? "border-[#bfdbfe]" : "border-dashed border-[#cbd5e1]"}`}
                                >
                                  {phone ? (
                                    <>
                                      <div className="min-w-0">
                                        {canSeeFullDetails() ? (
                                          <div className="text-[11px] font-mono font-extrabold text-[#0f172a]">
                                            {phone.phone}
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() =>
                                              handleDialSpecificPhone(
                                                phone.phone,
                                                `${u.firstName} ${u.lastName}`,
                                              )
                                            }
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0] hover:bg-[#d1fae5] transition-all font-extrabold text-[10px]"
                                          >
                                            <i className="fas fa-phone-alt text-[9px]" /> CALL
                                          </button>
                                        )}
                                        {phone.label && (
                                          <div className="text-[8px] text-[#64748b] font-mono">
                                            {phone.label}
                                          </div>
                                        )}
                                        {phone.isPrimary === 1 && (
                                          <span className="text-[7px] px-1.5 py-0.5 rounded bg-[#059669] text-white font-mono font-extrabold mt-1 inline-block">
                                            PRIMARY
                                          </span>
                                        )}
                                      </div>
                                      {canSeeFullDetails() && (
                                        <button
                                          onClick={() =>
                                            copyPhoneToClipboard(phone.phone)
                                          }
                                          className="w-7 h-7 rounded-lg bg-[#eff6ff] border border-[#bfdbfe] text-[#1e3a5f] hover:bg-[#dbeafe] flex items-center justify-center flex-shrink-0 transition-all"
                                          title="Copy number"
                                        >
                                          <i className="fas fa-copy text-[10px]" />
                                        </button>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-[10px] text-[#94a3b8] font-mono">
                                      Slot {slotIndex + 1} — Empty
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {leadPhones.length < 3 && (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={newPhoneInput}
                                onChange={(e) =>
                                  setNewPhoneInput(e.target.value)
                                }
                                placeholder="Phone number"
                                className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] placeholder:text-[#94a3b8]"
                              />
                              <input
                                type="text"
                                value={newPhoneLabel}
                                onChange={(e) =>
                                  setNewPhoneLabel(e.target.value)
                                }
                                placeholder="Label (optional)"
                                className="w-32 bg-white border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] placeholder:text-[#94a3b8]"
                              />
                              <button
                                onClick={handleAddPhone}
                                disabled={addingPhone || !newPhoneInput.trim()}
                                className="px-4 py-2 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] disabled:opacity-50 transition-all border-b-[2px] border-[#047857] whitespace-nowrap"
                              >
                                {addingPhone ? (
                                  <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <i className="fas fa-plus mr-1" /> ADD
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {showPasswordField && hasPerm("canEditUser") && (
                        <div className="col-span-4 mt-2 p-4 bg-[#eff6ff] border-2 border-[#1e3a5f] rounded-xl">
                          <label className="text-[10px] font-mono font-extrabold text-[#1e3a5f] uppercase tracking-wider mb-2 block">
                            NEW PASSWORD
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Min 8 chars"
                              className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                            />
                            <button
                              onClick={() => {
                                if (newPassword.length >= 8)
                                  handleUpdateUser("password", newPassword);
                                else
                                  dispatch(
                                    showToast({
                                      message: "Password must be 8+ chars",
                                      type: "error",
                                    }),
                                  );
                              }}
                              className="px-5 py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                            >
                              RESET
                            </button>
                            <button
                              onClick={() => {
                                setShowPasswordField(false);
                                setNewPassword("");
                              }}
                              className="px-5 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-sm font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] transition-all"
                            >
                              CANCEL
                            </button>
                          </div>
                        </div>
                      )}
                      {editField &&
                        editField !== "all" &&
                        editField !== "password" &&
                        hasPerm("canEditUser") && (
                          <div className="col-span-4 mt-2 p-4 bg-[#eff6ff] border-2 border-[#1e3a5f] rounded-xl">
                            <label className="text-[10px] font-mono font-extrabold text-[#1e3a5f] uppercase tracking-wider mb-2 block">
                              EDIT {editField.toUpperCase()}
                            </label>
                            <div className="flex gap-2">
                              {editField === "tag" ? (
                                <select
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                                >
                                  {TAG_OPTIONS.map((t) => (
                                    <option key={t} value={t}>
                                      {t.toUpperCase()}
                                    </option>
                                  ))}
                                </select>
                              ) : editField === "leverage" ? (
                                <select
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                                >
                                  {LEVERAGE_OPTIONS.map((l) => (
                                    <option key={l} value={l}>
                                      {l}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="flex-1 bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                                />
                              )}
                              <button
                                onClick={() =>
                                  handleUpdateUser(editField, editValue)
                                }
                                className="px-5 py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                              >
                                SAVE
                              </button>
                              <button
                                onClick={() => setEditField(null)}
                                className="px-5 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-sm font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] transition-all"
                              >
                                CANCEL
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* RIGHT SIDE — Comments Panel */}
                <div
                  className="bg-white border-2 border-[#e2e8f0] rounded-xl shadow-sm flex flex-col"
                  style={{ maxHeight: "calc(100vh - 200px)" }}
                >
                  <div className="flex items-center gap-3 px-5 py-4 border-b-2 border-[#e2e8f0] bg-[#f8fafc] flex-shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-[#f0fdfa] border-2 border-[#99f6e4] flex items-center justify-center">
                      <i className="fas fa-comments text-[#0d9488] text-sm" />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-[#0b1e33] text-sm uppercase tracking-wider">
                        AnzoTrade COMMENTS
                      </h3>
                      <p className="text-[10px] text-[#475569] font-mono font-bold">
                        {leadData
                          ? `Lead: #${leadData.short_id}`
                          : "No lead linked"}
                      </p>
                    </div>
                    {leadData && getLeadStatusBadge(leadData.status)}
                    {leadData && (
                      <select
                        value={newCommentStatus}
                        onChange={(e) => handleStatusChangeOnly(e.target.value)}
                        disabled={isAddingComment}
                        className="ml-auto bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-1.5 text-[10px] font-extrabold outline-none focus:border-[#0d9488] cursor-pointer disabled:opacity-50"
                      >
                        <option value="">CHANGE STATUS</option>
                        {LEAD_STATUS_OPTIONS.map((s) => (
                          <option
                            className="bg-white text-black"
                            key={s}
                            value={s}
                          >
                            {s === "new_lead"
                              ? "NEW LEAD"
                              : s.replace(/_/g, " ").toUpperCase()}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                    {isLoadingComments ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-6 h-6 border-[3px] border-[#0d9488] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : leadComments.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <i className="fas fa-comment-dots text-[#cbd5e1] text-3xl mb-3" />
                        <p className="text-[#64748b] font-mono text-xs font-bold">
                          No comments yet
                        </p>
                        <p className="text-[#94a3b8] font-mono text-[9px] mt-1">
                          Add a comment to the AnzoTrade Lead
                        </p>
                      </div>
                    ) : (
                      leadComments.map((comment, idx) => (
                        <div
                          key={comment.id || idx}
                          className="flex items-start gap-3 p-3 rounded-xl bg-[#f8fafc] border border-[#e2e8f0]"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0f766e] to-[#0d9488] flex items-center justify-center flex-shrink-0">
                            <span className="text-[9px] font-extrabold text-white font-mono">
                              {comment.staffName?.[0]?.toUpperCase() || "A"}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[11px] font-syne font-extrabold text-[#0f172a]">
                                {comment.staffName || "Admin"}
                              </span>
                              <span className="text-[9px] text-[#94a3b8] font-mono">
                                {formatCommentDate(comment.createdAt)}
                              </span>
                            </div>
                            <p className="text-[11px] text-[#475569] font-bold leading-relaxed whitespace-pre-wrap">
                              {comment.comment}
                            </p>
                            {comment.status && (
                              <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded text-[8px] font-mono font-extrabold uppercase bg-[#f0fdfa] text-[#0d9488] border border-[#99f6e4]">
                                →{" "}
                                {comment.status
                                  .replace(/_/g, " ")
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="px-4 py-3 border-t-2 border-[#e2e8f0] bg-[#f8fafc] flex-shrink-0">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment to AnzoTrade Lead..."
                      rows={3}
                      className="w-full bg-white border-2 border-[#cbd5e1] rounded-xl px-4 py-2.5 text-[#0f172a] text-xs font-extrabold outline-none focus:border-[#0d9488] focus:bg-white resize-none transition-all placeholder:text-[#94a3b8]"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[9px] text-[#94a3b8] font-mono">
                        Press Enter to send, Shift+Enter for new line
                      </span>
                      <button
                        onClick={handleAddComment}
                        disabled={
                          isAddingComment ||
                          (!newComment.trim() && !newCommentStatus)
                        }
                        className="px-5 py-2 rounded-xl bg-[#0d9488] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0f766e] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-b-[3px] border-[#0f766e] flex items-center gap-1.5"
                      >
                        {isAddingComment ? (
                          <>
                            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                            SENDING...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane text-[9px]" /> ADD
                            COMMENT
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── TRADES TAB ── */}
          {activeTab === "trades" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-base">
                  TRADES
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={refreshUserDetails}
                    disabled={isRefreshing}
                    className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-xs font-mono font-extrabold hover:bg-[#0b1e33] transition-all disabled:opacity-50 uppercase tracking-wider border-b-[2px] border-[#0b1e33]"
                  >
                    <i
                      className={`fas fa-sync-alt mr-1.5 ${isRefreshing ? "animate-spin" : ""}`}
                    />
                    REFRESH
                  </button>
                  {hasPerm("canPlaceTrade") && (
                    <button
                      onClick={() => setActiveTab("actions")}
                      className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                    >
                      <i className="fas fa-plus mr-1.5" /> PLACE TRADE
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-0 mb-4 border-b-2 border-[#e2e8f0]">
                <button
                  onClick={() => setTradeSubTab("open")}
                  className={`px-5 py-3 text-xs font-syne font-extrabold transition-all border-b-[3px] uppercase tracking-wider ${tradeSubTab === "open" ? "border-[#059669] text-[#059669]" : "border-transparent text-[#94a3b8] hover:text-[#475569]"}`}
                >
                  <i className="fas fa-circle-check mr-1.5" />
                  OPEN ({positions.length})
                </button>
                <button
                  onClick={() => setTradeSubTab("closed")}
                  className={`px-5 py-3 text-xs font-syne font-extrabold transition-all border-b-[3px] uppercase tracking-wider ${tradeSubTab === "closed" ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-[#94a3b8] hover:text-[#475569]"}`}
                >
                  <i className="fas fa-clock-rotate-left mr-1.5" />
                  CLOSED ({closedTrades.length})
                </button>
              </div>

              {tradeSubTab === "open" &&
                (positions.length === 0 ? (
                  <div className="text-center py-16 text-[#64748b] font-mono font-bold text-xs uppercase tracking-wider">
                    No open positions
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-left text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                          <th className="px-4 py-3.5">SYMBOL</th>
                          <th className="px-4 py-3.5">SIDE</th>
                          <th className="px-4 py-3.5">LOTS</th>
                          <th className="px-4 py-3.5">ENTRY</th>
                          <th className="px-4 py-3.5">CURRENT</th>
                          <th className="px-4 py-3.5">OPEN P&L</th>
                          <th className="px-4 py-3.5">SWAP</th>
                          <th className="px-4 py-3.5">S/L</th>
                          <th className="px-4 py-3.5">T/P</th>
                          <th className="px-4 py-3.5">MARGIN</th>
                          <th className="px-4 py-3.5">DATE</th>
                          {hasPerm("canModifyTrade") && (
                            <th className="px-4 py-3.5">ACTIONS</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {positions.map((pos) => {
                          const qty = Math.abs(Number(pos.qty)) || 0;
                          const avgPrice = Number(pos.avgPrice) || 0;
                          const cost = Number(pos.cost) || 0;
                          const cat = pos.category || "crypto";
                          const isShort =
                            pos.direction === "sell" || Number(pos.qty) < 0;
                          const currentPrice = getPrice(
                            pos.instrumentId,
                            pos.avgPrice,
                          );
                          const upnl = calcOpenPositionPnl(pos, currentPrice);
                          const swapAmt =
                            Number(pos.swap) || Number(pos.totalSwap) || 0;
                          const netPnl = upnl - swapAmt;
                          const netPnlPct =
                            cost > 0 ? (netPnl / cost) * 100 : 0;
                          const isEditing = editPositionId === pos.id;
                          return (
                            <tr
                              key={pos.id || pos.instrumentId}
                              className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all"
                            >
                              <td className="px-4 py-3 font-extrabold text-[#0f172a] text-xs">
                                {pos.instrumentId}
                              </td>
                              <td className="px-4 py-3">
                                {isEditing ? (
                                  <select
                                    value={editPositionDirection}
                                    onChange={(e) =>
                                      setEditPositionDirection(e.target.value)
                                    }
                                    className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-[10px] text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  >
                                    <option value="buy">LONG</option>
                                    <option value="sell">SHORT</option>
                                  </select>
                                ) : (
                                  <span
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${isShort ? "bg-[#dc2626] text-white border-[#b91c1c]" : "bg-[#059669] text-white border-[#047857]"}`}
                                  >
                                    {isShort ? "SHORT" : "LONG"}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-xs font-extrabold text-[#0f172a]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editPositionQty}
                                    onChange={(e) =>
                                      setEditPositionQty(e.target.value)
                                    }
                                    step="0.01"
                                    className="w-20 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : (
                                  qty.toFixed(2)
                                )}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#0f172a]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editPositionEntry}
                                    onChange={(e) =>
                                      setEditPositionEntry(e.target.value)
                                    }
                                    step="any"
                                    className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : (
                                  fmtP(avgPrice, cat)
                                )}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#1e3a5f]">
                                {fmtP(currentPrice, cat)}
                              </td>
                              <td
                                className={`px-4 py-3 font-extrabold text-xs ${netPnl >= 0 ? "text-[#059669]" : "text-[#dc2626]"}`}
                              >
                                {netPnl >= 0 ? "+" : ""}
                                {fmtUSD(netPnl)} ({netPnlPct >= 0 ? "+" : ""}
                                {netPnlPct.toFixed(2)}%)
                              </td>
                              <td className="px-4 py-3 font-extrabold text-xs text-[#d97706]">
                                {swapAmt !== 0
                                  ? "-$" + swapAmt.toFixed(2)
                                  : "$0.00"}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#f87171]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editPositionSl}
                                    onChange={(e) =>
                                      setEditPositionSl(e.target.value)
                                    }
                                    step="any"
                                    className="w-20 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : Number(pos.sl) > 0 ? (
                                  fmtP(Number(pos.sl), cat)
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#34d399]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editPositionTp}
                                    onChange={(e) =>
                                      setEditPositionTp(e.target.value)
                                    }
                                    step="any"
                                    className="w-20 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : Number(pos.tp) > 0 ? (
                                  fmtP(Number(pos.tp), cat)
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#2563eb]">
                                {fmtUSD(cost)}
                              </td>
                              <td className="px-4 py-3 text-[10px] text-[#64748b] font-mono font-bold">
                                {isEditing ? (
                                  <input
                                    type="datetime-local"
                                    value={editPositionTime}
                                    onChange={(e) =>
                                      setEditPositionTime(e.target.value)
                                    }
                                    className="w-44 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-[10px] text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : pos.createdAt ? (
                                  new Date(pos.createdAt).toLocaleDateString()
                                ) : (
                                  "—"
                                )}
                              </td>
                              {hasPerm("canModifyTrade") && (
                                <td className="px-4 py-3">
                                  <div className="flex gap-1.5">
                                    {isEditing ? (
                                      <>
                                        <button
                                          onClick={() =>
                                            handleSavePosition(pos.id)
                                          }
                                          className="px-3 py-1.5 rounded-lg bg-[#059669] text-white text-[10px] font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all"
                                        >
                                          ✓
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditPositionId(null);
                                            setEditPositionEntry("");
                                            setEditPositionQty("");
                                            setEditPositionDirection("");
                                            setEditPositionTime("");
                                            setEditPositionSl("");
                                            setEditPositionTp("");
                                          }}
                                          className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all"
                                        >
                                          ✕
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => {
                                            setEditPositionId(pos.id);
                                            setEditPositionEntry(
                                              String(avgPrice),
                                            );
                                            setEditPositionQty(String(qty));
                                            setEditPositionDirection(
                                              isShort ? "sell" : "buy",
                                            );
                                            setEditPositionTime(
                                              pos.createdAt
                                                ? new Date(pos.createdAt)
                                                    .toISOString()
                                                    .slice(0, 16)
                                                : "",
                                            );
                                            setEditPositionSl(
                                              Number(pos.sl) > 0
                                                ? String(Number(pos.sl))
                                                : "",
                                            );
                                            setEditPositionTp(
                                              Number(pos.tp) > 0
                                                ? String(Number(pos.tp))
                                                : "",
                                            );
                                          }}
                                          className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                                        >
                                          EDIT
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleClosePosition(pos.id)
                                          }
                                          className="px-3 py-1.5 rounded-lg bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b45309] transition-all border-b-[2px] border-[#b45309]"
                                        >
                                          CLOSE
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleDeletePosition(pos.id)
                                          }
                                          disabled={
                                            deletingPositionId === pos.id
                                          }
                                          className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all border-b-[2px] border-[#b91c1c]"
                                        >
                                          {deletingPositionId === pos.id
                                            ? "..."
                                            : "DEL"}
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}

              {tradeSubTab === "closed" &&
                (closedTrades.length === 0 ? (
                  <div className="text-center py-16 text-[#64748b] font-mono font-bold text-xs uppercase tracking-wider">
                    No closed trades
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-left text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                          <th className="px-4 py-3.5">SYMBOL</th>
                          <th className="px-4 py-3.5">SIDE</th>
                          <th className="px-4 py-3.5">LOTS</th>
                          <th className="px-4 py-3.5">ENTRY</th>
                          <th className="px-4 py-3.5">EXIT</th>
                          <th className="px-4 py-3.5">P&L</th>
                          <th className="px-4 py-3.5">SWAP</th>
                          <th className="px-4 py-3.5">NET</th>
                          <th className="px-4 py-3.5">OPEN TIME</th>
                          <th className="px-4 py-3.5">CLOSE TIME</th>
                          {hasPerm("canModifyTrade") && (
                            <th className="px-4 py-3.5">ACTIONS</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {closedTrades.map((trade) => {
                          const pnl = Number(trade.pnl) || 0;
                          const swap = Number(trade.swap) || 0;
                          const net = pnl - swap;
                          const cat = trade.category || "crypto";
                          const isEditing = editTradeId === trade.id;
                          const isLong = trade.direction === "buy";
                          return (
                            <tr
                              key={trade.id}
                              className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all"
                            >
                              <td className="px-4 py-3 font-extrabold text-[#0f172a] text-xs">
                                {trade.instrumentId}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${isLong ? "bg-[#059669] text-white border-[#047857]" : "bg-[#dc2626] text-white border-[#b91c1c]"}`}
                                >
                                  {isLong ? "LONG" : "SHORT"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs font-extrabold text-[#0f172a]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editTradeQty}
                                    onChange={(e) => {
                                      setEditTradeQty(e.target.value);
                                      setEditPnl("");
                                    }}
                                    step="0.01"
                                    className="w-20 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : (
                                  Number(trade.qty).toFixed(2)
                                )}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#0f172a]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editEntryPrice}
                                    onChange={(e) => {
                                      setEditEntryPrice(e.target.value);
                                      setEditPnl("");
                                    }}
                                    step="any"
                                    className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : (
                                  fmtP(Number(trade.entryPrice), cat)
                                )}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#0f172a]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editExitPrice}
                                    onChange={(e) => {
                                      setEditExitPrice(e.target.value);
                                      setEditPnl("");
                                    }}
                                    step="any"
                                    className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : (
                                  fmtP(Number(trade.exitPrice || 0), cat)
                                )}
                              </td>
                              <td
                                className={`px-4 py-3 font-extrabold text-xs ${pnl >= 0 ? "text-[#059669]" : "text-[#dc2626]"}`}
                              >
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editPnl}
                                    onChange={(e) => setEditPnl(e.target.value)}
                                    placeholder="Auto"
                                    className="w-20 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : (
                                  <span>
                                    {pnl >= 0 ? "+" : ""}
                                    {pnl.toFixed(2)}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 font-extrabold text-xs text-[#d97706]">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editSwap}
                                    onChange={(e) =>
                                      setEditSwap(e.target.value)
                                    }
                                    className="w-20 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : swap > 0 ? (
                                  "-$" + swap.toFixed(2)
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td
                                className={`px-4 py-3 font-extrabold text-xs ${net >= 0 ? "text-[#059669]" : "text-[#dc2626]"}`}
                              >
                                {net >= 0 ? "+" : ""}
                                {net.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-[10px] text-[#64748b] font-mono font-bold">
                                {isEditing ? (
                                  <input
                                    type="datetime-local"
                                    value={editTradeOpenTime}
                                    onChange={(e) =>
                                      setEditTradeOpenTime(e.target.value)
                                    }
                                    className="w-40 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-[10px] text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : trade.openTime ? (
                                  new Date(trade.openTime).toLocaleString()
                                ) : trade.time ? (
                                  new Date(trade.time).toLocaleString()
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-4 py-3 text-[10px] text-[#64748b] font-mono font-bold">
                                {isEditing ? (
                                  <input
                                    type="datetime-local"
                                    value={editTradeCloseTime}
                                    onChange={(e) =>
                                      setEditTradeCloseTime(e.target.value)
                                    }
                                    className="w-40 bg-white border-2 border-[#cbd5e1] rounded-lg px-2 py-1.5 text-[10px] text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f]"
                                  />
                                ) : trade.closeTime ? (
                                  new Date(trade.closeTime).toLocaleString()
                                ) : (
                                  "—"
                                )}
                              </td>
                              {hasPerm("canModifyTrade") && (
                                <td className="px-4 py-3">
                                  {isEditing ? (
                                    <div className="flex gap-1.5">
                                      <button
                                        onClick={() =>
                                          handleSaveTrade(trade.id)
                                        }
                                        className="px-3 py-1.5 rounded-lg bg-[#059669] text-white text-[10px] font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all"
                                      >
                                        ✓
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditTradeId(null);
                                          setEditPnl("");
                                          setEditSwap("");
                                          setEditEntryPrice("");
                                          setEditExitPrice("");
                                          setEditTradeOpenTime("");
                                          setEditTradeCloseTime("");
                                          setEditTradeQty("");
                                        }}
                                        className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-1.5">
                                      <button
                                        onClick={() => {
                                          setEditTradeId(trade.id);
                                          setEditPnl(String(pnl));
                                          setEditSwap(String(swap));
                                          setEditEntryPrice(
                                            String(trade.entryPrice || 0),
                                          );
                                          setEditExitPrice(
                                            String(trade.exitPrice || 0),
                                          );
                                          setEditTradeOpenTime(
                                            trade.openTime
                                              ? new Date(trade.openTime)
                                                  .toISOString()
                                                  .slice(0, 16)
                                              : trade.time
                                                ? new Date(trade.time)
                                                    .toISOString()
                                                    .slice(0, 16)
                                                : "",
                                          );
                                          setEditTradeCloseTime(
                                            trade.closeTime
                                              ? new Date(trade.closeTime)
                                                  .toISOString()
                                                  .slice(0, 16)
                                              : "",
                                          );
                                          setEditTradeQty(
                                            String(trade.qty || 0),
                                          );
                                        }}
                                        className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                                      >
                                        EDIT
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleDeleteTrade(trade.id)
                                        }
                                        disabled={deletingTradeId === trade.id}
                                        className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all border-b-[2px] border-[#b91c1c]"
                                      >
                                        {deletingTradeId === trade.id
                                          ? "..."
                                          : "DEL"}
                                      </button>
                                    </div>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          )}

          {/* ── OVERNIGHT TAB ── */}
          {activeTab === "overnight" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-base flex items-center gap-2">
                  <i className="fas fa-moon text-[#d97706]" /> OVERNIGHT
                  POSITIONS
                </h3>
                <button
                  onClick={refreshUserDetails}
                  disabled={isRefreshing}
                  className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-xs font-mono font-extrabold hover:bg-[#0b1e33] transition-all disabled:opacity-50 uppercase tracking-wider border-b-[2px] border-[#0b1e33]"
                >
                  <i
                    className={`fas fa-sync-alt mr-1.5 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  REFRESH
                </button>
              </div>
              {overnightPositions.length === 0 ? (
                <div className="text-center py-16 text-[#64748b] font-mono font-bold text-xs uppercase tracking-wider">
                  No overnight positions
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-left text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                        <th className="px-4 py-3.5">SYMBOL</th>
                        <th className="px-4 py-3.5">SIDE</th>
                        <th className="px-4 py-3.5">LOTS</th>
                        <th className="px-4 py-3.5">ENTRY</th>
                        <th className="px-4 py-3.5">CURRENT</th>
                        <th className="px-4 py-3.5">NIGHTS</th>
                        <th className="px-4 py-3.5">SWAP/LOT</th>
                        <th className="px-4 py-3.5">ACCRUED</th>
                        {hasPerm("canApplySwap") && (
                          <th className="px-4 py-3.5">EXTRA SWAP</th>
                        )}
                        {hasPerm("canApplySwap") && (
                          <th className="px-4 py-3.5">ACTION</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {overnightPositions.map((pos) => {
                        const qty = Math.abs(Number(pos.qty)) || 0;
                        const avgPrice = Number(pos.avgPrice) || 0;
                        const cat = pos.category || "crypto";
                        const isLong =
                          pos.direction === "buy" && Number(pos.qty) > 0;
                        const isShort =
                          pos.direction === "sell" || Number(pos.qty) < 0;
                        const currentPrice = getPrice(
                          pos.instrumentId,
                          pos.avgPrice,
                        );
                        const nights = getNightsHeld(pos.createdAt);
                        const ratePerLot = isLong
                          ? getSwapRate(pos.instrumentId, true)
                          : getSwapRate(pos.instrumentId, false);
                        const accrued = nights * qty * ratePerLot;
                        return (
                          <tr
                            key={pos.id || pos.instrumentId}
                            className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all"
                          >
                            <td className="px-4 py-3 font-extrabold text-[#0f172a] text-xs">
                              {pos.instrumentId}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${isShort ? "bg-[#dc2626] text-white border-[#b91c1c]" : "bg-[#059669] text-white border-[#047857]"}`}
                              >
                                {isShort ? "SHORT" : "LONG"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs font-extrabold text-[#0f172a]">
                              {qty.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#0f172a]">
                              {fmtP(avgPrice, cat)}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#1e3a5f]">
                              {fmtP(currentPrice, cat)}
                            </td>
                            <td className="px-4 py-3 font-extrabold text-xs text-[#1e3a5f]">
                              {nights}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs font-extrabold text-[#d97706]">
                              {ratePerLot > 0 ? (
                                "$" + ratePerLot.toFixed(2)
                              ) : (
                                <span className="text-[#94a3b8]">INACTIVE</span>
                              )}
                            </td>
                            <td className="px-4 py-3 font-extrabold text-xs text-[#dc2626]">
                              {ratePerLot > 0 ? "-$" + accrued.toFixed(2) : "—"}
                            </td>
                            {hasPerm("canApplySwap") && (
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  value={
                                    extraSwapInputs[pos.instrumentId] || ""
                                  }
                                  onChange={(e) =>
                                    setExtraSwapInputs({
                                      ...extraSwapInputs,
                                      [pos.instrumentId]: e.target.value,
                                    })
                                  }
                                  placeholder="$0"
                                  className="w-24 bg-white border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#1e3a5f] placeholder:text-[#94a3b8]"
                                />
                              </td>
                            )}
                            {hasPerm("canApplySwap") && (
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleApplyExtraSwap(pos)}
                                  className="px-4 py-2 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                                >
                                  APPLY
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── DEPOSITS TAB ── */}
          {activeTab === "deposits" && (
            <div>
              <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-base mb-4">
                DEPOSIT HISTORY ({deposits.length})
              </h3>
              {deposits.length === 0 ? (
                <div className="text-center py-16 text-[#64748b] font-mono font-bold text-xs uppercase tracking-wider">
                  No deposits
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-left text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                        <th className="px-5 py-3.5">DATE</th>
                        <th className="px-5 py-3.5">AMOUNT</th>
                        <th className="px-5 py-3.5">METHOD</th>
                        <th className="px-5 py-3.5">STATUS</th>
                        <th className="px-5 py-3.5">REFERENCE</th>
                        <th className="px-5 py-3.5">PROOF</th>
                        {hasPerm("canApproveWithdrawal") && (
                          <th className="px-5 py-3.5">ACTIONS</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {deposits.map((d) => {
                        const isCompleted = d.status === "completed";
                        const isSending = emailSending[d.id];
                        const isSent = emailSent[d.id] || d.emailSent;
                        const details = d.methodDetails || {};
                        const hasProof =
                          d.method === "bank_transfer" ||
                          d.method === "custom_upi" ||
                          d.method === "smilepayz";
                        const proofExists =
                          !!details.screenshotPath || !!details.screenshot;
                        return (
                          <tr
                            key={d.id}
                            className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all"
                          >
                            <td className="px-5 py-3 text-[11px] text-[#475569] font-mono font-bold">
                              {d.time
                                ? new Date(d.time).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                : "—"}
                            </td>
                            <td className="px-5 py-3 font-extrabold text-[#059669] text-[12px]">
                              +{fmtUSD(d.amount)}
                            </td>
                            <td className="px-5 py-3 text-[#0f172a] font-bold text-xs uppercase">
                              {d.method === "custom_upi"
                                ? "UPI 2"
                                : d.method === "smilepayz"
                                  ? "UPI 1"
                                  : d.method === "payalma"
                                    ? "AnzoTrade Pay"
                                    : d.method
                                      ? d.method
                                          .replace(/_/g, " ")
                                          .replace(/\b\w/g, (c) =>
                                            c.toUpperCase(),
                                          )
                                      : "—"}
                            </td>
                            <td className="px-5 py-3">
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${
                                  d.status === "completed"
                                    ? "bg-[#059669] text-white border-[#047857]"
                                    : d.status === "pending"
                                      ? "bg-[#d97706] text-white border-[#b45309]"
                                      : "bg-[#dc2626] text-white border-[#b91c1c]"
                                }`}
                              >
                                {d.status}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-[11px] text-[#475569] font-mono font-bold">
                              {d.transactionId || "—"}
                            </td>
                            <td className="px-5 py-3">
                              {hasProof && proofExists ? (
                                <button
                                  onClick={() => setViewDepositProof(d)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                                >
                                  <i className="fas fa-image text-[9px]" /> VIEW
                                  PROOF
                                </button>
                              ) : hasProof ? (
                                <span className="text-[10px] text-[#94a3b8] font-mono font-bold">
                                  NO PROOF
                                </span>
                              ) : (
                                <span className="text-[10px] text-[#94a3b8] font-mono font-bold">
                                  N/A
                                </span>
                              )}
                            </td>
                            {hasPerm("canApproveWithdrawal") && (
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-2">
                                  {d.status === "pending" && (
                                    <>
                                      <button
                                        onClick={() =>
                                          handleConfirmDeposit(d.id)
                                        }
                                        disabled={!!depositActionLoading[d.id]}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all disabled:opacity-50 border-b-[2px] border-[#047857]"
                                      >
                                        {depositActionLoading[d.id] ===
                                        "confirming" ? (
                                          <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <>
                                            <i className="fas fa-check text-[9px]" />{" "}
                                            CONFIRM
                                          </>
                                        )}
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleRejectDeposit(d.id)
                                        }
                                        disabled={!!depositActionLoading[d.id]}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 border-b-[2px] border-[#b91c1c]"
                                      >
                                        {depositActionLoading[d.id] ===
                                        "rejecting" ? (
                                          <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                          <>
                                            <i className="fas fa-times text-[9px]" />{" "}
                                            REJECT
                                          </>
                                        )}
                                      </button>
                                    </>
                                  )}
                                  {isCompleted && (
                                    <button
                                      onClick={() => handleSendEmail(d.id)}
                                      disabled={isSending || isSent}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all disabled:opacity-50 border-b-[2px] ${
                                        isSent
                                          ? "bg-[#f1f5f9] text-[#94a3b8] border-[#e2e8f0] cursor-not-allowed"
                                          : "bg-[#2563eb] text-white border-[#1d4ed8] hover:bg-[#1d4ed8]"
                                      }`}
                                    >
                                      {isSending ? (
                                        <>
                                          <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                                          SENDING...
                                        </>
                                      ) : isSent ? (
                                        <>
                                          <i className="fas fa-check text-[9px]" />{" "}
                                          SENT
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-envelope text-[9px]" />{" "}
                                          SEND EMAIL
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── WITHDRAWALS TAB ── */}
          {activeTab === "withdrawals" && (
            <div>
              <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-base mb-4">
                WITHDRAWAL HISTORY ({withdrawals.length})
              </h3>
              {withdrawals.length === 0 ? (
                <div className="text-center py-16 text-[#64748b] font-mono font-bold text-xs uppercase tracking-wider">
                  No withdrawals
                </div>
              ) : (
                <div className="space-y-3">
                  {withdrawals.map((w) => {
                    const details = w.methodDetails || {};
                    const isExpanded = expandedWithdrawal === w.id;
                    const isBank = w.method === "bank";
                    const isCrypto = w.method === "crypto";
                    const isLoad = withdrawalActionLoading[w.id];
                    const isCompleted = w.status === "completed";
                    const isWSending = withdrawalEmailSending[w.id];
                    const isWSent = withdrawalEmailSent[w.id] || w.emailSent;
                    return (
                      <div
                        key={w.id}
                        className="bg-white border-2 border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm"
                      >
                        <div
                          className="flex items-center px-5 py-4 cursor-pointer hover:bg-[#f8fafc] transition-all"
                          onClick={() =>
                            setExpandedWithdrawal(isExpanded ? null : w.id)
                          }
                        >
                          <div className="flex-1 grid grid-cols-4 gap-4 items-center">
                            <div className="text-[11px] text-[#475569] font-mono font-bold">
                              {w.time
                                ? new Date(w.time).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                : "—"}
                            </div>
                            <div className="font-extrabold text-[#dc2626] text-[12px]">
                              -{fmtUSD(w.amount)}
                            </div>
                            <div className="text-[#0f172a] font-bold text-xs uppercase">
                              {w.method}
                            </div>
                            <div>
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${
                                  w.status === "completed"
                                    ? "bg-[#059669] text-white border-[#047857]"
                                    : w.status === "pending"
                                      ? "bg-[#d97706] text-white border-[#b45309]"
                                      : "bg-[#dc2626] text-white border-[#b91c1c]"
                                }`}
                              >
                                {w.status}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 ml-4">
                            {hasPerm("canApproveWithdrawal") &&
                              w.status === "pending" && (
                                <div
                                  className="flex gap-1.5"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      handleApproveWithdrawal(w.id)
                                    }
                                    disabled={!!isLoad}
                                    className="px-3 py-1.5 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] disabled:opacity-50 border-b-[2px] border-[#047857]"
                                  >
                                    {isLoad === "approving" ? "..." : "APPROVE"}
                                  </button>
                                  <button
                                    onClick={() => handleRejectWithdrawal(w.id)}
                                    disabled={!!isLoad}
                                    className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] disabled:opacity-50 border-b-[2px] border-[#b91c1c]"
                                  >
                                    {isLoad === "rejecting" ? "..." : "REJECT"}
                                  </button>
                                </div>
                              )}
                            {isCompleted && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => openWithdrawalEmailModal(w.id)}
                                  disabled={isWSending || isWSent}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all disabled:opacity-50 border-b-[2px] ${
                                    isWSent
                                      ? "bg-[#f1f5f9] text-[#94a3b8] border-[#e2e8f0] cursor-not-allowed"
                                      : "bg-[#dc2626] text-white border-[#b91c1c] hover:bg-[#b91c1c]"
                                  }`}
                                >
                                  {isWSending ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                                      SENDING...
                                    </>
                                  ) : isWSent ? (
                                    <>
                                      <i className="fas fa-check text-[9px]" />{" "}
                                      SENT
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-envelope text-[9px]" />{" "}
                                      SEND EMAIL
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                            <i
                              className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-[#64748b] text-xs`}
                            />
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t-2 border-[#e2e8f0] px-5 py-4 bg-[#f8fafc]">
                            <div className="grid grid-cols-2 gap-4">
                              {isBank && (
                                <>
                                  <div>
                                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                                      ACCOUNT NAME
                                    </div>
                                    <div className="text-[12px] text-[#0f172a] font-mono font-bold">
                                      {details.accountName ||
                                        details.accountHolder ||
                                        "—"}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                                      ACCOUNT NUMBER
                                    </div>
                                    <div className="text-[12px] text-[#0f172a] font-mono font-bold">
                                      {details.accountNumber || "—"}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                                      IFSC CODE
                                    </div>
                                    <div className="text-[12px] text-[#0f172a] font-mono font-bold">
                                      {details.ifscCode || "—"}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                                      BANK NAME
                                    </div>
                                    <div className="text-[12px] text-[#0f172a] font-mono font-bold">
                                      {details.bankName || "—"}
                                    </div>
                                  </div>
                                  {details.branchAddress && (
                                    <div className="col-span-2">
                                      <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                                        BRANCH ADDRESS
                                      </div>
                                      <div className="text-[12px] text-[#0f172a] font-mono font-bold">
                                        {details.branchAddress}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                              {isCrypto && (
                                <>
                                  <div>
                                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                                      NETWORK
                                    </div>
                                    <div className="text-[12px] text-[#0f172a] font-mono font-bold">
                                      {details.network || "—"}
                                    </div>
                                  </div>
                                  <div className="col-span-2">
                                    <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">
                                      WALLET ADDRESS
                                    </div>
                                    <div className="text-[12px] text-[#0f172a] font-mono font-bold break-all">
                                      {details.walletAddress || "—"}
                                    </div>
                                  </div>
                                </>
                              )}
                              {!isBank && !isCrypto && (
                                <div className="col-span-2">
                                  <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-2">
                                    METHOD DETAILS
                                  </div>
                                  <pre className="text-[11px] text-[#0f172a] font-mono font-bold bg-[#f1f5f9] rounded-xl p-3 overflow-x-auto border border-[#e2e8f0]">
                                    {JSON.stringify(details, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {w.status === "rejected" && w.rejectionReason && (
                                <div className="col-span-2 mt-1 p-3 bg-[#fef2f2] border-2 border-[#fecaca] rounded-xl">
                                  <div className="text-[9px] text-[#dc2626] font-mono font-extrabold uppercase tracking-wider mb-1">
                                    REJECTION REASON
                                  </div>
                                  <div className="text-[11px] text-[#dc2626] font-mono font-bold">
                                    {w.rejectionReason}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── KYC TAB ── */}
          {activeTab === "kyc" && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-base flex items-center gap-2">
                  <i className="fas fa-id-card text-[#1e3a5f]" /> KYC DOCUMENTS
                </h3>
                <div className="flex items-center gap-3">
                  <div className="text-[10px] text-[#475569] font-mono font-extrabold">
                    {documents.length} / {kycDocTypes.length} UPLOADED
                  </div>
                  {hasPerm("canEditUser") &&
                    documents.some(
                      (d) => d.status !== "approved" && d.status !== "rejected",
                    ) && (
                      <button
                        onClick={() => setShowApproveAllConfirm(true)}
                        className="px-4 py-2 rounded-lg bg-[#059669] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all border-b-[2px] border-[#047857] flex items-center gap-1.5"
                      >
                        <i className="fas fa-check-double text-[9px]" /> APPROVE
                        ALL KYC
                      </button>
                    )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {kycDocTypes.map(({ type, icon, label, verifiedKey }) => {
                  const doc = documents.find((d) => d.type === type);
                  const verified = verifiedKey ? !!u[verifiedKey] : false;
                  const isApprovingThis =
                    kycActionLoading[type] === "approving";
                  const isRejectingThis =
                    kycActionLoading[type] === "rejecting";
                  return (
                    <div
                      key={type}
                      className={`p-5 rounded-xl border-2 transition-all ${
                        verified
                          ? "border-[#059669] bg-[#f0fdf4]"
                          : doc
                            ? doc.status === "rejected"
                              ? "border-[#dc2626] bg-[#fef2f2]"
                              : "border-[#d97706] bg-[#fef9c3]"
                            : "border-[#e2e8f0] bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg shrink-0 border-2 ${
                              verified
                                ? "bg-[#059669] text-white border-[#047857]"
                                : doc
                                  ? doc.status === "rejected"
                                    ? "bg-[#dc2626] text-white border-[#b91c1c]"
                                    : "bg-[#d97706] text-white border-[#b45309]"
                                  : "bg-[#f1f5f9] text-[#94a3b8] border-[#e2e8f0]"
                            }`}
                          >
                            <i className={`fas ${icon}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-extrabold text-[#0f172a] text-sm uppercase tracking-wider">
                              {label}
                            </div>
                            {doc ? (
                              <div className="text-[10px] text-[#475569] font-mono font-bold truncate mt-0.5">
                                {doc.originalName || doc.filename}
                              </div>
                            ) : (
                              <div className="text-[10px] text-[#94a3b8] font-mono font-bold mt-0.5">
                                NOT UPLOADED
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="shrink-0">
                          {verified ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-[#059669] text-white text-[9px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#047857]">
                              <i className="fas fa-check-circle text-[8px]" />{" "}
                              VERIFIED
                            </span>
                          ) : doc ? (
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-[9px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${
                                doc.status === "approved"
                                  ? "bg-[#059669] text-white border-[#047857]"
                                  : doc.status === "rejected"
                                    ? "bg-[#dc2626] text-white border-[#b91c1c]"
                                    : "bg-[#d97706] text-white border-[#b45309]"
                              }`}
                            >
                              <i
                                className={`fas ${doc.status === "approved" ? "fa-check-circle" : doc.status === "rejected" ? "fa-times-circle" : "fa-clock"} text-[8px]`}
                              />
                              {doc.status === "approved"
                                ? "APPROVED"
                                : doc.status === "rejected"
                                  ? "REJECTED"
                                  : "PENDING"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-[#f1f5f9] text-[#94a3b8] text-[9px] font-mono font-extrabold uppercase tracking-wider border-2 border-[#e2e8f0]">
                              <i className="fas fa-cloud-upload-alt text-[8px]" />{" "}
                              NOT UPLOADED
                            </span>
                          )}
                        </div>
                      </div>
                      {doc?.uploadedAt && (
                        <div className="mt-3 text-[9px] text-[#64748b] font-mono font-bold">
                          UPLOADED: {new Date(doc.uploadedAt).toLocaleString()}
                        </div>
                      )}
                      {doc?.status === "rejected" && doc?.rejectionReason && (
                        <div className="mt-2 px-3 py-2 bg-[#fef2f2] border-2 border-[#fecaca] rounded-lg">
                          <div className="text-[8px] text-[#dc2626] font-mono font-extrabold uppercase mb-0.5">
                            REJECTION REASON
                          </div>
                          <div className="text-[10px] text-[#dc2626] font-mono font-bold">
                            {doc.rejectionReason}
                          </div>
                        </div>
                      )}
                      {doc && (
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={() => setKycViewDoc(doc)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold hover:bg-[#0b1e33] transition-all uppercase tracking-wider border-b-[2px] border-[#0b1e33]"
                          >
                            <i className="fas fa-eye text-[9px]" /> VIEW
                            DOCUMENT
                          </button>
                          {!verified &&
                            doc.status === "pending" &&
                            hasPerm("canEditUser") && (
                              <>
                                <button
                                  onClick={() => handleKycApprove(doc)}
                                  disabled={isApprovingThis || isRejectingThis}
                                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold hover:bg-[#047857] transition-all disabled:opacity-50 uppercase tracking-wider border-b-[2px] border-[#047857]"
                                >
                                  {isApprovingThis ? (
                                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <i className="fas fa-check text-[9px]" />{" "}
                                      APPROVE
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    setKycViewDoc({
                                      ...doc,
                                      _forceReject: true,
                                    })
                                  }
                                  disabled={isApprovingThis || isRejectingThis}
                                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold hover:bg-[#b91c1c] transition-all disabled:opacity-50 uppercase tracking-wider border-b-[2px] border-[#b91c1c]"
                                >
                                  <i className="fas fa-xmark text-[9px]" />{" "}
                                  REJECT
                                </button>
                              </>
                            )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 p-5 bg-white border-2 border-[#e2e8f0] rounded-xl grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-extrabold text-[#059669]">
                    {documents.filter((d) => d.status === "approved").length}
                  </div>
                  <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mt-1">
                    APPROVED
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-[#d97706]">
                    {documents.filter((d) => d.status === "pending").length}
                  </div>
                  <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mt-1">
                    PENDING
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-[#dc2626]">
                    {documents.filter((d) => d.status === "rejected").length}
                  </div>
                  <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mt-1">
                    REJECTED
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── QUESTIONNAIRE TAB ── */}
          {activeTab === "questionnaire" && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-base flex items-center gap-2">
                  <i className="fas fa-clipboard-list text-[#1e3a5f]" /> TRADING
                  QUESTIONNAIRE
                </h3>
                <div className="text-[10px] text-[#475569] font-mono font-extrabold">
                  {u.questionnaire ? "Completed" : "Not Completed"}
                </div>
              </div>
              {!u.questionnaire ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-20 h-20 rounded-full bg-[#f1f5f9] border-2 border-[#e2e8f0] flex items-center justify-center mb-4">
                    <i className="fas fa-clipboard-list text-[#94a3b8] text-2xl" />
                  </div>
                  <h4 className="font-extrabold text-[#0f172a] text-sm uppercase tracking-wider mb-2">
                    No Questionnaire Data
                  </h4>
                  <p className="text-[#64748b] text-xs font-mono font-bold">
                    This user has not completed the trading questionnaire yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    let q;
                    try {
                      q =
                        typeof u.questionnaire === "string"
                          ? JSON.parse(u.questionnaire)
                          : u.questionnaire;
                    } catch (e) {
                      return (
                        <div className="p-4 bg-[#fef2f2] border-2 border-[#fecaca] rounded-xl">
                          <p className="text-[#dc2626] font-mono font-bold text-xs">
                            Error parsing questionnaire data.
                          </p>
                        </div>
                      );
                    }
                    const questions = [
                      {
                        page: "1/6",
                        question: "Main sources of funds available for trading",
                      },
                      {
                        page: "2/6",
                        question:
                          "Amount expected to deposit in the next 12 months (USD)",
                      },
                      {
                        page: "3/6",
                        question: "Total Annual Estimated Income (USD)",
                      },
                      {
                        page: "4/6",
                        question: "Are you a Politically Exposed Person (PEP)?",
                      },
                      {
                        page: "5/6",
                        question: "Nature of profession/business",
                      },
                      {
                        page: "6/6",
                        question:
                          "Do you understand CFDs carry significant risk?",
                      },
                    ];
                    return questions.map((item, idx) => {
                      const answer = q[item.page];
                      const isAnswered =
                        answer !== undefined &&
                        answer !== null &&
                        answer !== "";
                      return (
                        <div
                          key={item.page}
                          className={`p-4 rounded-xl border-2 transition-all ${isAnswered ? "bg-[#f0fdf4] border-[#059669]" : "bg-[#fef2f2] border-[#dc2626]"}`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-extrabold shrink-0 border-2 ${isAnswered ? "bg-[#059669] text-white border-[#047857]" : "bg-[#dc2626] text-white border-[#b91c1c]"}`}
                            >
                              {isAnswered ? (
                                <i className="fas fa-check" />
                              ) : (
                                <i className="fas fa-xmark" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[9px] font-mono font-extrabold text-[#64748b] uppercase tracking-wider">
                                  {item.page}
                                </span>
                                {isAnswered ? (
                                  <span className="text-[8px] px-1.5 py-0.5 rounded font-mono font-extrabold bg-[#059669] text-white border-b-[2px] border-[#047857] uppercase">
                                    ANSWERED
                                  </span>
                                ) : (
                                  <span className="text-[8px] px-1.5 py-0.5 rounded font-mono font-extrabold bg-[#dc2626] text-white border-b-[2px] border-[#b91c1c] uppercase">
                                    NOT ANSWERED
                                  </span>
                                )}
                              </div>
                              <p className="font-extrabold text-[#0f172a] text-sm mb-2">
                                {item.question}
                              </p>
                              {isAnswered ? (
                                <div className="bg-white border border-[#cbd5e1] rounded-lg px-3 py-2">
                                  <span className="text-xs text-[#0f172a] font-mono font-bold">
                                    {answer}
                                  </span>
                                </div>
                              ) : (
                                <p className="text-[#94a3b8] text-xs font-mono font-bold italic">
                                  No answer provided
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ── ACTIONS TAB ── */}
          {activeTab === "actions" && hasPerm("canViewActions") && (
            <div className="space-y-6">
              {hasPerm("canChangePassword") && (
                <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                  <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-sm mb-4 flex items-center gap-2">
                    <i className="fas fa-key text-[#1e3a5f]" /> RESET PASSWORD
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password (min 8 chars)"
                      className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                    />
                    <button
                      onClick={() => handleUpdateUser("password", newPassword)}
                      className="px-5 py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                    >
                      RESET
                    </button>
                  </div>
                </div>
              )}

              {hasPerm("canPlaceTrade") && (
                <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                  <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-sm mb-4 flex items-center gap-2">
                    <i className="fas fa-exchange-alt text-[#1e3a5f]" /> PLACE
                    TRADE
                  </h3>
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setTradeMode("open")}
                      className={`flex-1 py-3 rounded-xl text-xs font-syne font-extrabold transition-all border-2 uppercase tracking-wider ${tradeMode === "open" ? "bg-[#059669] text-white border-[#047857] border-b-[3px] border-[#047857]" : "bg-[#f8fafc] text-[#94a3b8] border-[#e2e8f0] hover:text-[#475569] hover:border-[#cbd5e1]"}`}
                    >
                      <i className="fas fa-circle-play mr-1.5" /> OPEN TRADE
                    </button>
                    <button
                      onClick={() => setTradeMode("closed")}
                      className={`flex-1 py-3 rounded-xl text-xs font-syne font-extrabold transition-all border-2 uppercase tracking-wider ${tradeMode === "closed" ? "bg-[#1e3a5f] text-white border-[#0b1e33] border-b-[3px] border-[#0b1e33]" : "bg-[#f8fafc] text-[#94a3b8] border-[#e2e8f0] hover:text-[#475569] hover:border-[#cbd5e1]"}`}
                    >
                      <i className="fas fa-circle-check mr-1.5" /> CLOSED TRADE
                    </button>
                  </div>
                  {tradeMode === "open" && (
                    <div>
                      <p className="text-[10px] text-[#475569] font-mono font-bold mb-3">
                        <i className="fas fa-info-circle text-[#059669] mr-1.5" />
                        Creates a live position — appears in Active tab. Margin
                        blocked from balance.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            SYMBOL
                          </label>
                          <select
                            value={openTradeForm.instrumentId}
                            onChange={(e) => {
                              const inst = INSTRUMENTS.find(
                                (i) => i.id === e.target.value,
                              );
                              setOpenTradeForm({
                                ...openTradeForm,
                                instrumentId: e.target.value,
                                category: inst?.category || "crypto",
                              });
                            }}
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#059669] transition-all"
                          >
                            {Object.entries(groupedInstruments).map(
                              ([cat, insts]) => (
                                <optgroup
                                  key={cat}
                                  label={categoryLabels[cat] || cat}
                                >
                                  {insts.map((inst) => (
                                    <option key={inst.id} value={inst.id}>
                                      {inst.id}
                                    </option>
                                  ))}
                                </optgroup>
                              ),
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            DIRECTION
                          </label>
                          <select
                            value={openTradeForm.direction}
                            onChange={(e) =>
                              setOpenTradeForm({
                                ...openTradeForm,
                                direction: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#059669] transition-all"
                          >
                            <option value="buy">BUY / LONG</option>
                            <option value="sell">SELL / SHORT</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            LOTS
                          </label>
                          <input
                            type="number"
                            value={openTradeForm.quantity}
                            onChange={(e) =>
                              setOpenTradeForm({
                                ...openTradeForm,
                                quantity: e.target.value,
                              })
                            }
                            step="0.01"
                            min="0.01"
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#059669] transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            ENTRY PRICE
                          </label>
                          <input
                            type="number"
                            value={openTradeForm.entryPrice}
                            onChange={(e) =>
                              setOpenTradeForm({
                                ...openTradeForm,
                                entryPrice: e.target.value,
                              })
                            }
                            step="any"
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#059669] transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            DATE/TIME
                          </label>
                          <input
                            type="datetime-local"
                            value={openTradeForm.time}
                            onChange={(e) =>
                              setOpenTradeForm({
                                ...openTradeForm,
                                time: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#059669] transition-all"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={handlePlaceOpenTrade}
                            className="w-full px-4 py-3 rounded-xl bg-[#059669] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all border-b-[3px] border-[#047857]"
                          >
                            <i className="fas fa-circle-play mr-1.5" /> OPEN
                            TRADE
                          </button>
                        </div>
                      </div>
                      {openTradeForm.entryPrice && (
                        <div className="mt-3 p-3 bg-[#ecfdf5] border-2 border-[#a7f3d0] rounded-xl">
                          <span className="text-[10px] text-[#475569] font-mono font-bold">
                            CURRENT P&L PREVIEW:{" "}
                          </span>
                          {(() => {
                            const p = getOpenTradePnlPreview();
                            if (p === null)
                              return (
                                <span className="text-[11px] text-[#64748b] font-bold">
                                  Waiting for price data...
                                </span>
                              );
                            return (
                              <span
                                className={`text-[12px] font-extrabold font-mono ${p >= 0 ? "text-[#059669]" : "text-[#dc2626]"}`}
                              >
                                {p >= 0 ? "+" : ""}
                                {fmtUSD(p)}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  {tradeMode === "closed" && (
                    <div>
                      <p className="text-[10px] text-[#475569] font-mono font-bold mb-3">
                        <i className="fas fa-info-circle text-[#1e3a5f] mr-1.5" />
                        Creates a historical trade record with fixed P&L — goes
                        to History tab.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            SYMBOL
                          </label>
                          <select
                            value={tradeForm.instrumentId}
                            onChange={(e) => {
                              const inst = INSTRUMENTS.find(
                                (i) => i.id === e.target.value,
                              );
                              setTradeForm({
                                ...tradeForm,
                                instrumentId: e.target.value,
                                category: inst?.category || "crypto",
                              });
                            }}
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          >
                            {Object.entries(groupedInstruments).map(
                              ([cat, insts]) => (
                                <optgroup
                                  key={cat}
                                  label={categoryLabels[cat] || cat}
                                >
                                  {insts.map((inst) => (
                                    <option key={inst.id} value={inst.id}>
                                      {inst.id}
                                    </option>
                                  ))}
                                </optgroup>
                              ),
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            DIRECTION
                          </label>
                          <select
                            value={tradeForm.direction}
                            onChange={(e) =>
                              setTradeForm({
                                ...tradeForm,
                                direction: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          >
                            <option value="buy">BUY / LONG</option>
                            <option value="sell">SELL / SHORT</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            LOTS
                          </label>
                          <input
                            type="number"
                            value={tradeForm.quantity}
                            onChange={(e) =>
                              setTradeForm({
                                ...tradeForm,
                                quantity: e.target.value,
                              })
                            }
                            step="0.01"
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            ENTRY
                          </label>
                          <input
                            type="number"
                            value={tradeForm.entryPrice}
                            onChange={(e) =>
                              setTradeForm({
                                ...tradeForm,
                                entryPrice: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            EXIT
                          </label>
                          <input
                            type="number"
                            value={tradeForm.exitPrice}
                            onChange={(e) =>
                              setTradeForm({
                                ...tradeForm,
                                exitPrice: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            P&L
                          </label>
                          <input
                            type="number"
                            value={tradeForm.pnl}
                            onChange={(e) =>
                              setTradeForm({
                                ...tradeForm,
                                pnl: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            SWAP
                          </label>
                          <input
                            type="number"
                            value={tradeForm.swap}
                            onChange={(e) =>
                              setTradeForm({
                                ...tradeForm,
                                swap: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                            DATE/TIME
                          </label>
                          <input
                            type="datetime-local"
                            value={tradeForm.time}
                            onChange={(e) =>
                              setTradeForm({
                                ...tradeForm,
                                time: e.target.value,
                              })
                            }
                            className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={handlePlaceClosedTrade}
                            className="w-full px-4 py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                          >
                            <i className="fas fa-check mr-1.5" /> PLACE TRADE
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasPerm("canAdjustBalance") && (
                <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                  <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-sm mb-4 flex items-center gap-2">
                    <i className="fas fa-dollar-sign text-[#1e3a5f]" /> ADJUST
                    BALANCE
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      id="balanceInput"
                      placeholder="New balance"
                      className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                    />
                    <button
                      onClick={() => {
                        const val =
                          document.getElementById("balanceInput").value;
                        if (val) handleUpdateUser("balance", val);
                      }}
                      className="px-5 py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                    >
                      SET BALANCE
                    </button>
                  </div>
                </div>
              )}

              {hasPerm("canEditUser") && (
                <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                  <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-sm mb-4 flex items-center gap-2">
                    <i className="fas fa-gift text-[#1e3a5f]" /> GIVE BONUS
                  </h3>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="number"
                      id="bonusAmount"
                      placeholder="Amount"
                      className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                    />
                    <input
                      type="text"
                      id="bonusReason"
                      placeholder="Reason"
                      className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      const amt = document.getElementById("bonusAmount").value;
                      if (!amt || Number(amt) <= 0) {
                        dispatch(
                          showToast({
                            message: "Enter valid amount",
                            type: "error",
                          }),
                        );
                        return;
                      }
                      await adminApi.post(`/admin/users/${userId}/bonus`, {
                        amount: Number(amt),
                        reason: document.getElementById("bonusReason").value,
                      });
                      dispatch(
                        showToast({ message: "Bonus given!", type: "success" }),
                      );
                      refreshUserDetails();
                    }}
                    className="w-full py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                  >
                    GIVE BONUS
                  </button>
                </div>
              )}

              {hasPerm("canEditUser") && (
                <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                  <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-sm mb-4 flex items-center gap-2">
                    <i className="fas fa-hand-holding-dollar text-[#1e3a5f]" />{" "}
                    GIVE CREDIT
                  </h3>
                  <p className="text-[10px] text-[#475569] font-mono font-bold mb-3">
                    Add credit directly to user balance without a credit
                    request.
                  </p>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="number"
                      id="creditAmount"
                      placeholder="Amount"
                      className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                    />
                    <input
                      type="text"
                      id="creditReason"
                      placeholder="Reason (optional)"
                      className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      const amt = document.getElementById("creditAmount").value;
                      const reason =
                        document.getElementById("creditReason").value ||
                        "Admin credit";
                      if (!amt || Number(amt) <= 0) {
                        dispatch(
                          showToast({
                            message: "Enter valid amount",
                            type: "error",
                          }),
                        );
                        return;
                      }
                      await adminApi.post(
                        `/admin/users/${userId}/give-credit`,
                        { amount: Number(amt), reason: reason },
                      );
                      dispatch(
                        showToast({
                          message: `Credit of $${Number(amt).toFixed(2)} given!`,
                          type: "success",
                        }),
                      );
                      refreshUserDetails();
                    }}
                    className="w-full py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                  >
                    <i className="fas fa-hand-holding-dollar mr-1.5" /> GIVE
                    CREDIT
                  </button>
                </div>
              )}

              {hasPerm("canEditUser") && (
                <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                  <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-sm mb-4 flex items-center gap-2">
                    <i className="fas fa-credit-card text-[#1e3a5f]" /> SET
                    CREDIT LIMIT
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      id="creditLimitInput"
                      placeholder="Credit limit"
                      defaultValue={u.creditLimit || 0}
                      className="flex-1 bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all placeholder:text-[#94a3b8]"
                    />
                    <button
                      onClick={async () => {
                        const val =
                          document.getElementById("creditLimitInput").value;
                        if (val === "") return;
                        await adminApi.put(`/admin/users/${userId}/update`, {
                          creditLimit: Number(val),
                        });
                        dispatch(
                          showToast({
                            message: "Credit limit updated",
                            type: "success",
                          }),
                        );
                        refreshUserDetails();
                      }}
                      className="px-5 py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                    >
                      SET LIMIT
                    </button>
                  </div>
                </div>
              )}

              {hasPerm("canEditUser") && (
                <div className="bg-white border-2 border-[#e2e8f0] rounded-xl p-5 shadow-sm">
                  <h3 className="font-extrabold text-[#0b1e33] uppercase tracking-tight text-sm mb-4 flex items-center gap-2">
                    <i className="fas fa-sliders text-[#1e3a5f]" /> LEVERAGE &
                    TAG
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                        LEVERAGE
                      </label>
                      <select
                        id="leverageInput"
                        defaultValue={u.leverage || "1:100"}
                        className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                      >
                        {LEVERAGE_OPTIONS.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                        TAG
                      </label>
                      <select
                        id="tagInput"
                        defaultValue={u.tag || "bronze"}
                        className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] text-sm font-extrabold outline-none focus:border-[#1e3a5f] transition-all"
                      >
                        {TAG_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const lev =
                        document.getElementById("leverageInput").value;
                      const t = document.getElementById("tagInput").value;
                      await adminApi.put(`/admin/users/${userId}/update`, {
                        leverage: lev,
                        tag: t,
                      });
                      dispatch(
                        showToast({
                          message: "Settings updated",
                          type: "success",
                        }),
                      );
                      refreshUserDetails();
                    }}
                    className="w-full mt-3 py-3 rounded-xl bg-[#1e3a5f] text-white text-sm font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[3px] border-[#0b1e33]"
                  >
                    SAVE SETTINGS
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── APPROVE ALL KYC CONFIRMATION MODAL ── */}
      {showApproveAllConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !approvingAllKyc && setShowApproveAllConfirm(false)}
          />
          <div className="relative bg-white border-2 border-[#059669] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            <div className="text-center mb-5">
              <div className="w-16 h-16 rounded-full bg-[#f0fdf4] border-[3px] border-[#059669] flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-check-double text-[#059669] text-2xl" />
              </div>
              <h3 className="font-syne font-extrabold text-[#0b1e33] text-lg uppercase tracking-tight">
                APPROVE ALL KYC
              </h3>
              <p className="text-[#475569] text-sm mt-2 leading-relaxed font-bold">
                This will approve all pending KYC documents for {u.firstName}{" "}
                {u.lastName}.
              </p>
              <p className="text-[#d97706] text-xs mt-2 font-mono font-extrabold uppercase tracking-wider">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveAllConfirm(false)}
                disabled={approvingAllKyc}
                className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne text-sm font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] disabled:opacity-50 transition-all"
              >
                CANCEL
              </button>
              <button
                onClick={handleApproveAllKyc}
                disabled={approvingAllKyc}
                className="flex-1 py-3 rounded-xl bg-[#059669] text-white font-syne text-sm font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all disabled:opacity-50 flex items-center justify-center gap-2 border-b-[3px] border-[#047857]"
              >
                {approvingAllKyc ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    APPROVING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-double" /> APPROVE ALL
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CLOSE POSITION CONFIRMATION MODAL ── */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !closeConfirmLoading && setShowCloseConfirm(null)}
          />
          <div className="relative bg-white border-2 border-[#dc2626] rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            <div className="text-center mb-5">
              <div className="w-16 h-16 rounded-full bg-[#fef2f2] border-[3px] border-[#dc2626] flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-[#dc2626] text-2xl" />
              </div>
              <h3 className="font-syne font-extrabold text-[#0b1e33] text-lg uppercase tracking-tight">
                CLOSE POSITION
              </h3>
              <p className="text-[#475569] text-sm mt-2 leading-relaxed font-bold">
                This will close the position at{" "}
                <span className="text-[#1e3a5f] font-extrabold">
                  current market price
                </span>{" "}
                for the user.
              </p>
              <p className="text-[#dc2626] text-xs mt-2 font-mono font-extrabold uppercase tracking-wider">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCloseConfirm(null)}
                disabled={closeConfirmLoading}
                className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne text-sm font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] disabled:opacity-50 transition-all"
              >
                CANCEL
              </button>
              <button
                onClick={confirmClosePosition}
                disabled={closeConfirmLoading}
                className="flex-1 py-3 rounded-xl bg-[#dc2626] text-white font-syne text-sm font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 flex items-center justify-center gap-2 border-b-[3px] border-[#b91c1c]"
              >
                {closeConfirmLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    CLOSING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-xmark" /> CLOSE POSITION
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT WITHDRAWAL MODAL ── */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() =>
              !withdrawalActionLoading[rejectWithdrawalId] &&
              setShowRejectModal(false)
            }
          />
          <div className="relative bg-white border-2 border-[#dc2626] rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="text-center mb-5">
              <div className="w-16 h-16 rounded-full bg-[#fef2f2] border-[3px] border-[#dc2626] flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-xmark text-[#dc2626] text-2xl" />
              </div>
              <h3 className="font-syne font-extrabold text-[#0b1e33] text-lg uppercase tracking-tight">
                REJECT WITHDRAWAL
              </h3>
              <p className="text-[#475569] text-sm mt-2 font-bold">
                Please provide a reason for rejecting this withdrawal request.
              </p>
            </div>
            <div className="mb-5">
              <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                REJECTION REASON <span className="text-[#dc2626]">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter the reason for rejection..."
                rows={4}
                autoFocus
                className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] font-mono text-sm font-extrabold outline-none focus:border-[#dc2626] focus:bg-white resize-none transition-all placeholder:text-[#94a3b8]"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={!!withdrawalActionLoading[rejectWithdrawalId]}
                className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#f1f5f9] disabled:opacity-50 transition-all"
              >
                CANCEL
              </button>
              <button
                onClick={confirmRejectWithdrawal}
                disabled={!!withdrawalActionLoading[rejectWithdrawalId]}
                className="flex-1 py-3 rounded-xl bg-[#dc2626] text-white font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 flex items-center justify-center gap-2 border-b-[3px] border-[#b91c1c]"
              >
                {withdrawalActionLoading[rejectWithdrawalId] === "rejecting" ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    REJECTING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-xmark" /> CONFIRM REJECTION
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── WITHDRAWAL SEND EMAIL MODAL ── */}
      {withdrawalEmailModal && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setWithdrawalEmailModal(null);
              setWithdrawalTransactionId("");
            }}
          />
          <div className="relative bg-white border-2 border-[#e2e8f0] rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="text-center mb-5">
              <div className="w-16 h-16 rounded-full bg-[#eff6ff] border-[3px] border-[#2563eb] flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-envelope text-[#2563eb] text-2xl" />
              </div>
              <h3 className="font-syne font-extrabold text-[#0b1e33] text-lg uppercase tracking-tight">
                SEND WITHDRAWAL EMAIL
              </h3>
              <p className="text-[#475569] text-sm mt-2 font-bold">
                Enter the Transaction ID to include in the withdrawal
                confirmation email.
              </p>
            </div>
            <div className="mb-5">
              <label className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider block mb-2">
                TRANSACTION ID <span className="text-[#dc2626]">*</span>
              </label>
              <input
                type="text"
                value={withdrawalTransactionId}
                onChange={(e) => setWithdrawalTransactionId(e.target.value)}
                placeholder="Enter bank transaction ID or reference"
                autoFocus
                className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] font-mono text-sm font-extrabold outline-none focus:border-[#2563eb] focus:bg-white transition-all placeholder:text-[#94a3b8]"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setWithdrawalEmailModal(null);
                  setWithdrawalTransactionId("");
                }}
                disabled={!!withdrawalEmailSending[withdrawalEmailModal]}
                className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#f1f5f9] disabled:opacity-50 transition-all"
              >
                CANCEL
              </button>
              <button
                onClick={handleSendWithdrawalEmail}
                disabled={!!withdrawalEmailSending[withdrawalEmailModal]}
                className="flex-1 py-3 rounded-xl bg-[#2563eb] text-white font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#1d4ed8] transition-all disabled:opacity-50 flex items-center justify-center gap-2 border-b-[3px] border-[#1d4ed8]"
              >
                {withdrawalEmailSending[withdrawalEmailModal] ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    SENDING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane" /> SEND EMAIL
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── KYC DOCUMENT VIEWER MODAL ── */}
      {kycViewDoc && (
        <KycDocumentModal
          doc={kycViewDoc}
          onClose={() => {
            setKycViewDoc(null);
            setKycApproving(false);
            setKycRejecting(false);
          }}
          onApprove={hasPerm("canEditUser") ? handleKycApprove : null}
          onReject={hasPerm("canEditUser") ? handleKycReject : null}
          isApproving={kycApproving}
          isRejecting={kycRejecting}
        />
      )}

      {/* ── DEPOSIT PROOF VIEWER MODAL ── */}
      {viewDepositProof && (
        <DepositProofModal
          deposit={viewDepositProof}
          onClose={() => setViewDepositProof(null)}
        />
      )}

      {/* CUSTOM INCOMING CALL WIDGET */}
      {incomingCall && (
        <div
          style={{ position: "fixed", bottom: 20, right: 20, zIndex: 10000 }}
          className="w-[320px] bg-white border-2 border-[#1e3a5f] rounded-2xl shadow-2xl overflow-hidden animate-bounce-in"
        >
          <div className="px-4 py-3 bg-[#0b1e33] text-white">
            <div className="text-[9px] font-mono font-extrabold uppercase tracking-wider text-[#a7f3d0] flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a7f3d0] animate-pulse" />{" "}
              INCOMING CALL
            </div>
            <div className="font-syne text-base font-extrabold mt-1 truncate">
              {incomingCall.lead?.name || "Unknown caller"}
            </div>
            {incomingCall.lead?.short_id && (
              <div className="text-[10px] font-mono font-bold text-[#bfdbfe]">
                #{incomingCall.lead.short_id}
              </div>
            )}
          </div>
          <div className="flex gap-2 p-3">
            <button
              onClick={declineCall}
              className="flex-1 py-2.5 rounded-xl bg-[#dc2626] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] border-b-[3px] border-[#b91c1c]"
            >
              <i className="fas fa-phone-slash mr-1.5" /> DECLINE
            </button>
            <button
              onClick={acceptCall}
              className="flex-1 py-2.5 rounded-xl bg-[#059669] text-white text-[11px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] border-b-[3px] border-[#047857]"
            >
              <i className="fas fa-phone-alt mr-1.5" /> ACCEPT
            </button>
          </div>
        </div>
      )}

      {/* CUSTOM END CALL BUTTON (outgoing calls) */}
      {isCallActive && !incomingCall && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
          <button
            onClick={handleHangup}
            className="px-6 py-3 rounded-full bg-[#dc2626] text-white font-syne font-extrabold uppercase tracking-wider shadow-2xl hover:bg-[#b91c1c] transition-all border-b-[3px] border-[#b91c1c] flex items-center gap-2"
          >
            <i className="fas fa-phone-slash text-lg" /> END CALL
          </button>
        </div>
      )}

      <style>{`
        @keyframes float { 0%,100% { transform:translate(0,0) } 50% { transform:translate(28px,-28px) } }
        .animate-float { animation: float 20s ease-in-out infinite }
        .animation-delay-2000 { animation-delay: -8s }
      `}</style>
    </div>
  );
};

export default UserDetailPage;